import React, { useEffect, useRef, useState, useCallback } from 'react'
import { Vehicle } from '../types'
import { apiClient } from '../config/api'
import { loadGoogleMapsScript } from '../hooks/useGoogleMapsLoader'
import { ChevronDown, MapPin, Route, Clock, Activity, BarChart3, Download, Printer, Share2, X, Calendar, Filter, Satellite, Map as MapIcon, Play, Pause, RotateCcw, SkipBack, SkipForward, Gauge, Timer, Database, TrendingUp, Navigation, AlertTriangle, Focus } from 'lucide-react'
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart, ReferenceLine, BarChart, Bar, PieChart, Pie, Cell } from 'recharts'
import CalendarButton from './CalendarButton'
import MapWarningsPanel from './MapWarningsPanel'
import { DateRange, todayRange } from './CalendarModal'
import { useSettings } from '../contexts/SettingsContext'
import { mapIconSvg, trackingPresentation } from '../utils/vehiclePresentation'
import { resolvePlaceName } from '../utils/placeNames'
import { useTranslation } from 'react-i18next'

export type PositionCreationMode = 'geofence' | 'trajectory'

export interface PositionCreationRequest {
  mode: PositionCreationMode
  point: { lat: number, lng: number }
  zoom: number
  referenceTrajectory: Array<{ lat: number, lng: number }>
  dateRange: { startDate: string, endDate: string }
  vehicle: Vehicle
}

interface ResponsiveFleetMapProps {
  vehicles: Vehicle[]
  selectedVehicle: Vehicle | null
  onVehicleSelect: (vehicle: Vehicle) => void
  timeRange?: number
  timelineHover?: { timestamp: string | null, position: { lat: number, lng: number } | null }
  onTimelineHover?: (timestamp: string | null, position: { lat: number, lng: number } | null) => void
  isDrawingMode?: boolean
  drawingType?: 'trajectory' | 'circle' | 'square' | 'triangle' | 'polygon' | 'point'
  onDrawingComplete?: (points: Array<{ lat: number, lng: number }>) => void
  sharedDateRange?: DateRange // Accept shared date range from parent
  onDateRangeChange?: (range: DateRange) => void // Callback to update parent's date range
  onCreateFromPosition?: (request: PositionCreationRequest) => void
  onCreateAlert?: (vehicle: Vehicle) => void
  onOpenAlertDetail?: (alertId: number) => void
}

const ResponsiveFleetMap: React.FC<ResponsiveFleetMapProps> = ({ 
  vehicles, 
  selectedVehicle, 
  onVehicleSelect,
  timeRange = 8760,
  timelineHover,
  onTimelineHover,
  isDrawingMode = false,
  drawingType = 'trajectory',
  onDrawingComplete,
  sharedDateRange,
  onDateRangeChange,
  onCreateFromPosition,
  onCreateAlert,
  onOpenAlertDetail
}) => {
  const { t } = useTranslation()
  const { get: getSetting } = useSettings()
  const preferredMapStyle = String(getSetting('workspace.map_style', 'streets'))
  const markerRefreshSeconds = Number(getSetting('refresh.map_seconds', 30))
  const defaultMapZoom = Number(getSetting('workspace.map_default_zoom', 12))
  const defaultMapLatitude = Number(getSetting('workspace.map_center_latitude', 14.7167))
  const defaultMapLongitude = Number(getSetting('workspace.map_center_longitude', -17.4677))
  const mapRef = useRef<HTMLDivElement>(null)
  const mapInstanceRef = useRef<google.maps.Map | null>(null)
  const markersRef = useRef<Map<string, google.maps.marker.AdvancedMarkerElement>>(new Map())
  const markerAnimationsRef = useRef<Map<string, number>>(new Map())
  const trajectoryRef = useRef<google.maps.Polyline | null>(null)
  const timelineMarkerRef = useRef<google.maps.Marker | null>(null)
  const trajectorySegmentsRef = useRef<google.maps.Polyline[]>([])
  const trajectoryMarkersRef = useRef<google.maps.Marker[]>([])
  const eventMarkersRef = useRef<google.maps.Marker[]>([])
  const mapListenersRef = useRef<google.maps.MapsEventListener[]>([])
  const trajectoryDataRef = useRef<{ validPoints: any[], optimizedPoints: any[] } | null>(null)
  const activeTrajectoryVehicleRef = useRef<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'map' | 'speed' | 'stops' | 'timeline' | 'stats'>('map')
  const [showTabContent, setShowTabContent] = useState(false)
  const [speedData, setSpeedData] = useState<any[]>([])
  const [stopEvents, setStopEvents] = useState<any[]>([])
  const [eventData, setEventData] = useState<any[]>([])
  const [chartLoading, setChartLoading] = useState(false)
  const [replayIndex, setReplayIndex] = useState(0)
  const [replayPlaying, setReplayPlaying] = useState(false)
  const [replayRate, setReplayRate] = useState(1)
  const [showLegend, setShowLegend] = useState(false)
  // Opt-in follow mode: the camera glides with the selected vehicle (zoom
  // preserved). Turned off automatically when the user drags the map.
  const [followVehicle, setFollowVehicle] = useState(false)
  const followVehicleRef = useRef(false)
  // "This vehicle only": hide the rest of the fleet while a vehicle is
  // selected, so its route is not buried under other pucks (Nana, 12 Oct).
  const [soloVehicle, setSoloVehicle] = useState(false)
  const [visibleMarkerKinds, setVisibleMarkerKinds] = useState<Record<'event' | 'alert' | 'stop' | 'parking', boolean>>({
    event: true,
    alert: true,
    stop: true,
    parking: true
  })
  const [hoveredChartPoint, setHoveredChartPoint] = useState<{ timestamp: string | null, position: { lat: number, lng: number } | null }>({ timestamp: null, position: null })
  const [drawingPoints, setDrawingPoints] = useState<Array<{ lat: number, lng: number }>>([])
  const [drawingMarkers, setDrawingMarkers] = useState<google.maps.Marker[]>([])
  const [drawingPath, setDrawingPath] = useState<google.maps.Polyline | null>(null)
  const [isSatelliteView, setIsSatelliteView] = useState(false)
  const [warningsClosed, setWarningsClosed] = useState(
    () => sessionStorage.getItem('fleet_map_warnings_dismissed') === '1'
  )
  const [warningCount, setWarningCount] = useState(0)

  // Fixed workspaces and resizable Mac windows can change the map container
  // without remounting this component. Google Maps needs an explicit resize
  // notification to keep tiles and the current viewport aligned.
  useEffect(() => {
    const container = mapRef.current
    if (!container || typeof ResizeObserver === 'undefined') return
    let frame = 0
    const observer = new ResizeObserver(() => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        const map = mapInstanceRef.current
        if (map && window.google?.maps?.event) {
          window.google.maps.event.trigger(map, 'resize')
        }
      })
    })
    observer.observe(container)
    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
    }
  }, [])

  // Fleet-wide view: refit bounds when the SET of plotted vehicles changes
  // (filtering in the vehicle list), not on every position refresh.
  const hasFitBoundsRef = useRef(false)
  const plottedVehiclesSigRef = useRef('')
  const [trajectoryStats, setTrajectoryStats] = useState<{
    totalDistance: number
    totalDuration: number
    startTime: Date | null
    endTime: Date | null
    maxSpeed: number
    avgSpeed: number
    stopCount: number
  }>({
    totalDistance: 0,
    totalDuration: 0,
    startTime: null,
    endTime: null,
    maxSpeed: 0,
    avgSpeed: 0,
    stopCount: 0
  })

  // Use shared date range from parent or default
  const dateRange = sharedDateRange || todayRange()

  // Handle date range change
  const handleDateRangeChange = (newRange: DateRange) => {
    console.log(`📅 Fleet map date range changed to: ${newRange.startDate} - ${newRange.endDate}`)
    
    // Update parent's shared date range
    if (onDateRangeChange) {
      onDateRangeChange(newRange)
    }
  }

  // Clear all trajectory data from map
  const clearAllTrajectories = (preserveTimelineMarker = false) => {
    console.log('🧹 Clearing all trajectories from map')

    // Clear main trajectory reference
    if (trajectoryRef.current) {
      trajectoryRef.current.setMap(null)
      trajectoryRef.current = null
    }

    // Clear all trajectory segments
    trajectorySegmentsRef.current.forEach(segment => {
      if (segment) {
        segment.setMap(null)
      }
    })
    trajectorySegmentsRef.current = []

    // Clear all trajectory-related markers (stop events, start/end flags, vehicle icons)
    trajectoryMarkersRef.current.forEach(marker => {
      if (marker) {
        marker.setMap(null)
      }
    })
    trajectoryMarkersRef.current = []
    eventMarkersRef.current.forEach(marker => marker.setMap(null))
    eventMarkersRef.current = []
    clusterMarkersRef.current.forEach(marker => marker.setMap(null))
    clusterMarkersRef.current = []

    // Clear trajectory data
    trajectoryDataRef.current = null
    activeTrajectoryVehicleRef.current = null

    // Clear timeline marker
    if (!preserveTimelineMarker && timelineMarkerRef.current) {
      timelineMarkerRef.current.setMap(null)
      timelineMarkerRef.current = null
    }
    
    // Remove date range info
    const existingInfo = document.getElementById('date-range-info')
    if (existingInfo) {
      existingInfo.remove()
    }
    
    // Remove legend
    const existingLegend = document.getElementById('trajectory-legend')
    if (existingLegend) {
      existingLegend.remove()
    }
    
    // Remove legend button
    const existingButton = document.getElementById('legend-toggle-button')
    if (existingButton) {
      existingButton.remove()
    }
    
    console.log('✅ All trajectories cleared')
  }

  // Toggle satellite view
  const toggleSatelliteView = () => {
    if (!mapInstanceRef.current) return
    
    const newSatelliteState = !isSatelliteView
    setIsSatelliteView(newSatelliteState)
    
    if (newSatelliteState) {
      console.log('🛰️ Switching to satellite view')
      mapInstanceRef.current.setMapTypeId(google.maps.MapTypeId.HYBRID)
    } else {
      console.log('🗺️ Switching to roadmap view')
      mapInstanceRef.current.setMapTypeId(google.maps.MapTypeId.ROADMAP)
    }
  }

  // Load trajectory data with date range filtering
  const loadTrajectoryData = async (vehicleId: string, startDate: string, endDate: string) => {
    try {
      console.log(`🗺️ Loading trajectory data for vehicle: ${vehicleId} from ${startDate} to ${endDate}`)
      
      // Calculate hours from date range
      const startTime = new Date(startDate).getTime()
      const endTime = new Date(endDate).getTime()
      const hours = Math.ceil((endTime - startTime) / (1000 * 60 * 60))
      
      const response = await apiClient.get(`/timeline/${vehicleId}`, { 
        hours: hours
      })
      
      if (response.data && Array.isArray(response.data)) {
        console.log(`🗺️ Trajectory data received: ${response.data.length} points`)
        
        // Update trajectory on map
        if (mapInstanceRef.current) {
          // updateTrajectory(response.data) // Function not implemented yet
        }
        
        // Also update chart data
        loadChartData(vehicleId)
      }
    } catch (error) {
      console.error('❌ Error loading trajectory data:', error)
    }
  }


  // Stroke paths of the icon the vehicle was created with (pickup, tanker,
  // container...), so the map puck matches the glyph shown in the lists.
  const vehicleGlyph = (vehicle?: Vehicle) =>
    mapIconSvg(vehicle?.iconKey, vehicle?.assetInfo?.type, vehicle?.vehicleSource === 'CONTAINER')

  // Exact truck puck used by the CMA CGM interface. The legacy Google Maps
  // marker version is used for trajectory hover/replay points.
  const getCmaTruckIcon = (vehicle?: Vehicle, size = 30) => {
    const color = vehicle
      ? trackingPresentation(vehicle.trackingStatus, vehicle.currentSpeed).color
      : '#2D5BFF'
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 30 30"><circle cx="15" cy="15" r="13.5" fill="${color}" stroke="white" stroke-width="3"/><g transform="translate(6 6) scale(.75)" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${vehicleGlyph(vehicle)}</g></svg>`
    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
      scaledSize: new google.maps.Size(size, size),
      anchor: new google.maps.Point(size / 2, size / 2),
      labelOrigin: new google.maps.Point(size / 2, size + 8),
      origin: new google.maps.Point(0, 0)
    }
  }

  // The puck follows the zoom. A fixed 30 px made the vehicle the same size as
  // the stop and event badges strewn along its own route, so it was impossible
  // to pick out. The previous 34-64 px band hid the map under the pucks when
  // a fleet is parked in one yard (Nana, 12 Oct), so it now runs 22-40 px and
  // only the selected vehicle grows.
  const vehicleMarkerSize = (selected = false) => {
    const zoom = mapInstanceRef.current?.getZoom() ?? defaultMapZoom
    const base = Math.max(22, Math.min(40, 22 + (zoom - 11) * 3))
    return Math.round(selected ? base * 1.35 : base)
  }

  // Resizing on zoom only rewrites one CSS variable: the puck, its pulse and the
  // hover state are all derived from it, and rebuilding the markers would
  // restart the position animations.
  const resizeVehicleMarkers = () => {
    markersRef.current.forEach(marker => {
      const content = marker.content as HTMLElement | null
      if (!content?.style) return
      const selected = content.classList.contains('is-selected')
      content.style.setProperty('--marker-size', `${vehicleMarkerSize(selected)}px`)
    })
  }

  // Same circular truck puck, pulse and identifier used in CMA CGM.
  const createVehicleMarkerContent = (vehicle: Vehicle, size: number, selected: boolean) => {
    const tracking = trackingPresentation(vehicle.trackingStatus, vehicle.currentSpeed)
    const color = tracking.color
    const root = document.createElement('div')
    root.className = `sini-map-vehicle-marker ${vehicle.trackingStatus === 'LIVE' ? 'is-live' : ''} ${selected ? 'is-selected' : ''}`
    root.style.setProperty('--marker-color', color)
    root.style.setProperty('--marker-size', `${size}px`)

    const pulse = document.createElement('span')
    pulse.className = 'sini-map-vehicle-pulse'
    const puck = document.createElement('span')
    puck.className = 'sini-map-vehicle-puck'
    puck.style.color = '#FFFFFF'
    puck.innerHTML = `<svg viewBox="0 0 24 24" aria-hidden="true">${vehicleGlyph(vehicle)}</svg>`
    const label = document.createElement('span')
    label.className = 'sini-map-vehicle-label'
    label.textContent = vehicle.operationalIdentifier || vehicle.plateNumber || vehicle.name
    root.title = `${vehicle.name} · ${t(tracking.labelKey, { defaultValue: tracking.label })} · ${vehicle.lastUpdate ? new Date(vehicle.lastUpdate).toLocaleString() : 'No telemetry'}`
    root.append(pulse, puck, label)
    return root
  }

  // ---- Lucide-based activity marker badges ----
  // Google Maps marker icons need an image URL, so the official Lucide glyph
  // paths (24x24, stroke style) are embedded in data-URL SVGs: white glyph on
  // a colored circular badge, matching the lucide-react icons used in the UI.
  type BadgeKind = 'parking' | 'stop' | 'event' | 'alert' | 'start' | 'end'
  const LUCIDE_GLYPHS: Record<BadgeKind, string> = {
    // Lucide "square-parking" P glyph
    parking: '<path d="M9 17V7h4a3 3 0 0 1 0 6H9"/>',
    // Lucide "pause"
    stop: '<rect x="14" y="4" width="4" height="16" rx="1"/><rect x="6" y="4" width="4" height="16" rx="1"/>',
    // Lucide "activity"
    event: '<path d="M3 12h4l2-7 4 14 2-7h6"/>',
    // Lucide "triangle-alert"
    alert: '<path d="m21.7 18-8-14a2 2 0 0 0-3.4 0l-8 14a2 2 0 0 0 1.7 3h16a2 2 0 0 0 1.7-3Z"/><path d="M12 9v4"/><path d="M12 17h.01"/>',
    // Lucide "flag"
    start: '<path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" x2="4" y1="22" y2="15"/>',
    end: '<path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"/><line x1="4" x2="4" y1="22" y2="15"/>'
  }
  const BADGE_COLORS: Record<BadgeKind, string> = {
    parking: '#7C3AED',   // purple
    stop: '#F59E0B',      // amber
    event: '#2563EB',     // blue
    alert: '#DC2626',     // red
    start: '#16A34A',     // green
    end: '#DC2626'        // red
  }
  const lucideBadgeIcon = (kind: BadgeKind, size: number, count = 1): google.maps.Icon => {
    const countBadge = count > 1
      ? `<circle cx="51" cy="13" r="11" fill="#111827" stroke="#ffffff" stroke-width="3"/>` +
        `<text x="51" y="17" fill="#ffffff" font-size="12" font-family="Arial,sans-serif" font-weight="700" text-anchor="middle">${Math.min(count, 99)}</text>`
      : ''
    const svg =
      `<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64">` +
      `<circle cx="32" cy="32" r="29" fill="${BADGE_COLORS[kind]}" stroke="#ffffff" stroke-width="4"/>` +
      `<g transform="translate(14,14) scale(1.5)" fill="none" stroke="#ffffff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">${LUCIDE_GLYPHS[kind]}</g>` +
      countBadge +
      `</svg>`
    return {
      url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg),
      scaledSize: new google.maps.Size(size, size),
      // Anchor badges above their GPS coordinate so the route remains visible.
      anchor: new google.maps.Point(size / 2, size),
      origin: new google.maps.Point(0, 0)
    }
  }

  // Keep the CMA marker hierarchy stable at every zoom level.
  const updateMarkerSizesOnZoom = () => {
    // The vehicle grows with the zoom; the route badges keep a fixed size, which
    // is what makes the vehicle stand out among them.
    resizeVehicleMarkers()
    const apply = (marker: google.maps.Marker) => {
      const meta = (marker as any).__markerMeta
      if (!meta) return
      if (meta.type === 'badge') {
        const sizes: Record<BadgeKind, number> = {
          start: 27,
          end: 27,
          parking: 30,
          stop: 28,
          event: 26,
          alert: 28
        }
        const size = sizes[meta.kind as BadgeKind] || 23
        marker.setIcon(lucideBadgeIcon(meta.kind as BadgeKind, size, meta.count || 1))
      }
    }
    trajectoryMarkersRef.current.forEach(apply)
    eventMarkersRef.current.forEach(apply)
  }

  // ---- Zoomed-out clustering of route badges ----
  // Stops, parkings, events and alerts along one route pile up as soon as
  // the map is zoomed out (the airport screenshot of 12 Oct). Badges closer
  // than CLUSTER_PIXELS on screen collapse into one dark circle carrying the
  // count; clicking it zooms onto the group. Vehicle pucks and the start/end
  // flags are never clustered.
  const clusterMarkersRef = useRef<google.maps.Marker[]>([])
  const CLUSTER_PIXELS = 44
  const clusterIcon = (count: number): google.maps.Icon => {
    const size = count >= 100 ? 44 : count >= 10 ? 40 : 36
    const svg =
      `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">` +
      `<circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 2}" fill="#111827" fill-opacity="0.9" stroke="#ffffff" stroke-width="3"/>` +
      `<text x="${size / 2}" y="${size / 2 + 5}" fill="#ffffff" font-size="14" font-family="Arial,sans-serif" font-weight="700" text-anchor="middle">${count > 999 ? '999+' : count}</text>` +
      '</svg>'
    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
      scaledSize: new google.maps.Size(size, size),
      anchor: new google.maps.Point(size / 2, size / 2)
    }
  }
  const reclusterRouteBadges = () => {
    const map = mapInstanceRef.current
    if (!map || !window.google?.maps) return
    clusterMarkersRef.current.forEach(marker => marker.setMap(null))
    clusterMarkersRef.current = []
    const candidates = [...trajectoryMarkersRef.current, ...eventMarkersRef.current].filter(marker => {
      const meta = (marker as any).__markerMeta
      return meta?.type === 'badge' && meta.kind !== 'start' && meta.kind !== 'end' && markerShouldBeVisible(meta.kind as BadgeKind)
    })
    candidates.forEach(marker => { if (marker.getMap() !== map) marker.setMap(map) })
    if (candidates.length < 2) return

    const zoom = map.getZoom() ?? 15
    const centerLat = map.getCenter()?.lat() ?? 0
    const metersPerPixel = 156543.03392 * Math.cos(centerLat * Math.PI / 180) / Math.pow(2, zoom)
    const thresholdKm = CLUSTER_PIXELS * metersPerPixel / 1000
    const clusters: Array<{ lat: number, lng: number, members: google.maps.Marker[] }> = []
    candidates.forEach(marker => {
      const position = marker.getPosition()
      if (!position) return
      const lat = position.lat()
      const lng = position.lng()
      const hit = clusters.find(cluster => calculateDistance(cluster.lat, cluster.lng, lat, lng) <= thresholdKm)
      if (hit) hit.members.push(marker)
      else clusters.push({ lat, lng, members: [marker] })
    })

    clusters.filter(cluster => cluster.members.length >= 2).forEach(cluster => {
      cluster.members.forEach(marker => marker.setMap(null))
      const count = cluster.members.reduce((sum, marker) => sum + (((marker as any).__markerMeta?.count as number) || 1), 0)
      const marker = new google.maps.Marker({
        position: { lat: cluster.lat, lng: cluster.lng },
        map,
        icon: clusterIcon(count),
        title: t('map_controls.cluster_title', { defaultValue: '{{count}} events here. Click to zoom in.', count }),
        zIndex: 1200
      })
      marker.addListener('click', () => {
        const bounds = new google.maps.LatLngBounds()
        cluster.members.forEach(member => {
          const position = member.getPosition()
          if (position) bounds.extend(position)
        })
        const spanKm = calculateDistance(
          bounds.getNorthEast().lat(), bounds.getNorthEast().lng(),
          bounds.getSouthWest().lat(), bounds.getSouthWest().lng()
        )
        if (spanKm < 0.03) {
          // Everything sits on one spot: fitBounds would dive to street level.
          map.panTo({ lat: cluster.lat, lng: cluster.lng })
          map.setZoom(Math.min(20, (map.getZoom() ?? zoom) + 3))
        } else {
          map.fitBounds(bounds, 80)
        }
      })
      clusterMarkersRef.current.push(marker)
    })
  }

  // Map container ref callback to ensure DOM is ready
  const mapRefCallback = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      mapRef.current = node
      initializeMap()
    }
  }, [])

  const initializeMap = async () => {
    if (!mapRef.current) {
      console.log('⏳ Map container not ready yet...')
      return
    }

    try {
      setIsLoading(true)
      setError(null)

      // Always verify required libraries. Another map component may have
      // loaded the base API first without the Advanced Marker library.
      console.log('🗺️ Loading Google Maps API and required libraries...')
      await loadGoogleMapsScript(['geometry', 'marker'], 3, 1000)

      console.log('✅ Creating Google Maps instance...')
      createMap()
      
    } catch (err) {
      console.error('❌ Map initialization error:', err)
      setError(err instanceof Error ? err.message : 'Failed to initialize Google Maps')
    } finally {
      setIsLoading(false)
    }
  }

  const createMap = () => {
    if (!mapRef.current || !window.google?.maps) {
      console.error('❌ Map container or Google Maps not available')
      return
    }

    // Default center (West Africa - Mali region)
    let center = { lat: defaultMapLatitude, lng: defaultMapLongitude }
    
    // Use first vehicle with valid coordinates as center
    const firstVehicle = vehicles.find(v => 
      v.location?.latitude && 
      v.location?.longitude && 
      !isNaN(v.location.latitude) && 
      !isNaN(v.location.longitude)
    )
    
    if (firstVehicle) {
      center = {
        lat: firstVehicle.location.latitude,
        lng: firstVehicle.location.longitude
      }
      console.log(`🎯 Centering map on vehicle: ${firstVehicle.name} at ${center.lat}, ${center.lng}`)
    }

    // Create map with responsive options
    const mapOptions: google.maps.MapOptions = {
      center,
      zoom: Math.max(1, Math.min(20, defaultMapZoom)),
      mapId: 'DEMO_MAP_ID',
      mapTypeId: preferredMapStyle === 'satellite'
        ? google.maps.MapTypeId.HYBRID
        : google.maps.MapTypeId.ROADMAP,
      styles: [
        {
          featureType: 'poi',
          elementType: 'labels',
          stylers: [{ visibility: 'off' }]
        }
      ],
      // Remove Google Maps UI controls to avoid duplicate menus with Mac taskbar
      zoomControl: false,
      mapTypeControl: false,
      scaleControl: false,
      streetViewControl: false,
      rotateControl: false,
      fullscreenControl: false,
      gestureHandling: 'greedy' // Better for responsive design
    }

    mapInstanceRef.current = new google.maps.Map(mapRef.current, mapOptions)
    console.log('✅ Google Maps instance created')

    // Keep all icon sizes in step with the zoom level
    const sizeListener = mapInstanceRef.current.addListener('zoom_changed', () => {
      updateMarkerSizesOnZoom()
    })
    mapListenersRef.current.push(sizeListener)

    // Manual drag means the user wants control: stop auto-following.
    const dragListener = mapInstanceRef.current.addListener('dragstart', () => {
      setFollowVehicle(false)
    })
    mapListenersRef.current.push(dragListener)

    // Plot the whole fleet as soon as the map is ready
    if (vehicles.some(v => vehicleHasValidPosition(v))) {
      updateVehicleMarkers(true)
      hasFitBoundsRef.current = true
    }

    // Load trajectory for selected vehicle
    if (selectedVehicle) {
      loadVehicleTrajectory(selectedVehicle.id)
    }

    // Set up drawing mode if enabled
    if (isDrawingMode) {
      enableDrawingMode()
    }
  }

  // Enable drawing mode for trajectory creation
  const enableDrawingMode = () => {
    if (!mapInstanceRef.current) return

    console.log(`🎨 Enabling drawing mode: ${drawingType}`)
    
    // Clear any existing drawing
    clearDrawing()
    
    // Change cursor to crosshair
    mapInstanceRef.current.setOptions({ 
      draggableCursor: 'crosshair',
      draggingCursor: 'crosshair'
    })

    // Add click listener for drawing
    const clickListener = mapInstanceRef.current.addListener('click', (event: google.maps.MapMouseEvent) => {
      if (event.latLng) {
        const point = {
          lat: event.latLng.lat(),
          lng: event.latLng.lng()
        }
        addDrawingPoint(point)
      }
    })

    // Store listener for cleanup
    mapListenersRef.current.push(clickListener)
  }

  // Add a point to the drawing
  const addDrawingPoint = (point: { lat: number, lng: number }) => {
    console.log(`📍 Adding drawing point:`, point)
    
    setDrawingPoints(prev => {
      const newPoints = [...prev, point]
      
      // Create marker for this point
      const marker = new google.maps.Marker({
        position: point,
        map: mapInstanceRef.current,
        icon: {
          url: '/assets/location-pin.png',
          scaledSize: new google.maps.Size(20, 20),
          anchor: new google.maps.Point(10, 20)
        },
        title: `Point ${newPoints.length}`,
        zIndex: 3000
      })

      // Add click listener to remove point
      marker.addListener('click', () => {
        removeDrawingPoint(newPoints.length - 1)
      })

      setDrawingMarkers(prevMarkers => [...prevMarkers, marker])
      
      // Update drawing path
      updateDrawingPath(newPoints)
      
      return newPoints
    })
  }

  // Remove a drawing point
  const removeDrawingPoint = (index: number) => {
    setDrawingPoints(prev => {
      const newPoints = prev.filter((_, i) => i !== index)
      updateDrawingPath(newPoints)
      return newPoints
    })
    
    // Remove corresponding marker
    setDrawingMarkers(prev => {
      if (prev[index]) {
        prev[index].setMap(null)
      }
      return prev.filter((_, i) => i !== index)
    })
  }

  // Update the drawing path polyline
  const updateDrawingPath = (points: Array<{ lat: number, lng: number }>) => {
    if (!mapInstanceRef.current) return

    // Remove existing path
    if (drawingPath) {
      drawingPath.setMap(null)
    }

    // Create new path if we have at least 2 points
    if (points.length >= 2) {
      const newPath = new google.maps.Polyline({
        path: points,
        geodesic: true,
        strokeColor: '#10B981',
        strokeOpacity: 0.8,
        strokeWeight: 4,
        zIndex: 2000
      })

      newPath.setMap(mapInstanceRef.current)
      setDrawingPath(newPath)
    }
  }

  // Clear all drawing elements
  const clearDrawing = () => {
    // Clear markers
    drawingMarkers.forEach(marker => marker.setMap(null))
    setDrawingMarkers([])
    
    // Clear path
    if (drawingPath) {
      drawingPath.setMap(null)
      setDrawingPath(null)
    }
    
    // Clear points
    setDrawingPoints([])
    
    // Reset cursor
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setOptions({ 
        draggableCursor: '',
        draggingCursor: ''
      })
    }
  }

  // Complete drawing and return points
  const completeDrawing = () => {
    if (drawingPoints.length >= 2) {
      console.log(`✅ Drawing completed with ${drawingPoints.length} points`)
      if (onDrawingComplete) {
        onDrawingComplete(drawingPoints)
      }
      clearDrawing()
    } else {
      alert('Please add at least 2 points to create a trajectory')
    }
  }

  // A vehicle is plottable when it has a real position (skip null/0,0)
  const vehicleHasValidPosition = (vehicle: Vehicle) => {
    const lat = vehicle.location?.latitude
    const lng = vehicle.location?.longitude
    return lat != null && lng != null && !isNaN(lat) && !isNaN(lng) && !(lat === 0 && lng === 0)
  }

  const animateVehicleMarker = (
    marker: google.maps.marker.AdvancedMarkerElement,
    vehicleId: string,
    from: { lat: number, lng: number },
    to: { lat: number, lng: number },
    previousUpdate?: string | Date | null,
    nextUpdate?: string | Date | null,
    isLive = false
  ) => {
    const running = markerAnimationsRef.current.get(vehicleId)
    if (running != null) cancelAnimationFrame(running)

    const latDistance = (to.lat - from.lat) * 111
    const lngDistance = (to.lng - from.lng) * 111 * Math.cos(to.lat * Math.PI / 180)
    const distanceKm = Math.sqrt(latDistance ** 2 + lngDistance ** 2)

    // Follow mode: glide the camera with the selected vehicle (zoom kept).
    // Ignore sub-5m GPS jitter so a parked vehicle keeps the map still.
    if (
      followVehicleRef.current &&
      selectedVehicle?.id === vehicleId &&
      distanceKm > 0.005 &&
      mapInstanceRef.current
    ) {
      mapInstanceRef.current.panTo(to)
    }

    if (!isLive || distanceKm === 0 || distanceKm > 20 || document.visibilityState !== 'visible') {
      marker.position = to
      ;(marker as any).__displayedPosition = to
      markerAnimationsRef.current.delete(vehicleId)
      return
    }

    const eventDelta = previousUpdate && nextUpdate
      ? new Date(nextUpdate).getTime() - new Date(previousUpdate).getTime()
      : NaN
    const fallbackDuration = Math.max(1000, markerRefreshSeconds * 900)
    const duration = Math.max(
      1000,
      Math.min(30_000, Number.isFinite(eventDelta) && eventDelta > 0 ? eventDelta : fallbackDuration)
    )
    const started = performance.now()
    const frame = (now: number) => {
      const progress = Math.min(1, (now - started) / duration)
      const position = {
        lat: from.lat + (to.lat - from.lat) * progress,
        lng: from.lng + (to.lng - from.lng) * progress
      }
      marker.position = position
      ;(marker as any).__displayedPosition = position
      if (progress < 1) {
        markerAnimationsRef.current.set(vehicleId, requestAnimationFrame(frame))
      } else {
        markerAnimationsRef.current.delete(vehicleId)
      }
    }
    markerAnimationsRef.current.set(vehicleId, requestAnimationFrame(frame))
  }

  const escapeHtml = (value: unknown) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')

  const vehicleInfoHtml = (vehicle: Vehicle) => {
    const vehicleId = JSON.stringify(vehicle.id)
    const position = vehicle.location
    const identifier = vehicle.operationalIdentifier || vehicle.plateNumber || vehicle.name
    const tracking = trackingPresentation(vehicle.trackingStatus, vehicle.speed)
    const mapPinIcon = '<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 10c0 5-8 12-8 12S4 15 4 10a8 8 0 1 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>'
    const routeIcon = '<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2"><circle cx="6" cy="19" r="3"/><path d="M9 19h6.5a3.5 3.5 0 0 0 0-7h-7a3.5 3.5 0 0 1 0-7H18"/><circle cx="18" cy="5" r="3"/></svg>'
    const historyIcon = '<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 12a9 9 0 1 0 3-6.7L3 8"/><path d="M3 3v5h5"/><path d="M12 7v5l3 2"/></svg>'
    const alertIcon = '<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2"><path d="M10.3 21h3.4"/><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9"/></svg>'
    return `
    <div style="min-width:290px;padding:4px 2px;color:#172033;font-family:Inter,Arial,sans-serif">
      <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:12px;margin-bottom:12px">
        <div>
          <div style="font-size:15px;font-weight:750">${escapeHtml(vehicle.name)}</div>
          <div style="margin-top:2px;color:#64748b;font-size:11px">${escapeHtml(identifier)}</div>
        </div>
        <span style="border-radius:999px;padding:4px 8px;background:${tracking.color}1a;color:${tracking.color};font-size:10px;font-weight:700">${escapeHtml(t(tracking.labelKey, { defaultValue: tracking.label }))}</span>
      </div>
      <div style="display:flex;align-items:center;gap:8px;border-radius:12px;background:#f8fafc;padding:9px 10px;margin-bottom:10px;color:#475569">
        ${mapPinIcon}
        <div>
          <div style="font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:.06em">${escapeHtml(t('current_position', { defaultValue: 'Current position' }))}</div>
          <div style="font-size:11px">${position.latitude.toFixed(5)}, ${position.longitude.toFixed(5)}</div>
        </div>
      </div>
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:7px 14px;font-size:12px;margin-bottom:12px">
        <div><span style="color:#94a3b8">${escapeHtml(t('speed', { defaultValue: 'Speed' }))}</span><strong style="display:block">${escapeHtml(vehicle.speed)} km/h</strong></div>
        <div><span style="color:#94a3b8">${escapeHtml(t('status', { defaultValue: 'Status' }))}</span><strong style="display:block">${escapeHtml(vehicle.status)}</strong></div>
        <div><span style="color:#94a3b8">${escapeHtml(t('battery', { defaultValue: 'Battery' }))}</span><strong style="display:block">${escapeHtml(vehicle.batteryLevel ?? 'N/A')}%</strong></div>
        <div><span style="color:#94a3b8">${escapeHtml(t('customer', { defaultValue: 'Customer' }))}</span><strong style="display:block;max-width:125px;overflow:hidden;text-overflow:ellipsis">${escapeHtml(vehicle.customerInfo?.name || '—')}</strong></div>
      </div>
      <div style="display:grid;grid-template-columns:repeat(4,1fr);gap:6px;border-top:1px solid #e2e8f0;padding-top:10px">
        <button onclick='window.createFromVehiclePosition && window.createFromVehiclePosition(${vehicleId}, "geofence")' style="display:flex;flex-direction:column;align-items:center;gap:4px;border:0;border-radius:11px;padding:8px 4px;background:#eef2ff;color:#4f46e5;font-size:10px;font-weight:700;cursor:pointer">
          ${mapPinIcon}<span>${escapeHtml(t('geofence', { defaultValue: 'Geofence' }))}</span>
        </button>
        <button onclick='window.createFromVehiclePosition && window.createFromVehiclePosition(${vehicleId}, "trajectory")' style="display:flex;flex-direction:column;align-items:center;gap:4px;border:0;border-radius:11px;padding:8px 4px;background:#ecfdf5;color:#059669;font-size:10px;font-weight:700;cursor:pointer">
          ${routeIcon}<span>${escapeHtml(t('itinerary', { defaultValue: 'Itinerary' }))}</span>
        </button>
        <button onclick='window.showTrajectory && window.showTrajectory(${vehicleId})' style="display:flex;flex-direction:column;align-items:center;gap:4px;border:0;border-radius:11px;padding:8px 4px;background:#fff7ed;color:#ea580c;font-size:10px;font-weight:700;cursor:pointer">
          ${historyIcon}<span>${escapeHtml(t('trajectory', { defaultValue: 'Trajectory' }))}</span>
        </button>
        <button onclick='window.createAlertForVehicle && window.createAlertForVehicle(${vehicleId})' style="display:flex;flex-direction:column;align-items:center;gap:4px;border:0;border-radius:11px;padding:8px 4px;background:#fef2f2;color:#dc2626;font-size:10px;font-weight:700;cursor:pointer">
          ${alertIcon}<span>${escapeHtml(t('alerts', { defaultValue: 'Alert' }))}</span>
        </button>
      </div>
      <div style="margin-top:9px;text-align:center;color:#94a3b8;font-size:9px">
        ${escapeHtml(t('live_route_refresh_hint', { defaultValue: 'Live route refreshes automatically' }))}
      </div>
    </div>
  `}

  const updateVehicleMarkers = (fitToVehicles = false) => {
    if (!mapInstanceRef.current || !window.google?.maps) return

    // Add markers for each vehicle with a real position. Filtering happens
    // upstream in the Fleet Vehicles list — the map plots what it receives.
    const shownVehicles = vehicles
      .filter(v => vehicleHasValidPosition(v))
      .filter(v => !soloVehicle || !selectedVehicle || v.id === selectedVehicle.id)
    const shownIds = new Set(shownVehicles.map(vehicle => vehicle.id))
    markersRef.current.forEach((marker, id) => {
      if (shownIds.has(id)) return
      const running = markerAnimationsRef.current.get(id)
      if (running != null) cancelAnimationFrame(running)
      markerAnimationsRef.current.delete(id)
      marker.map = null
      markersRef.current.delete(id)
    })

    shownVehicles.forEach(vehicle => {
      try {
      const position = {
        lat: vehicle.location.latitude,
        lng: vehicle.location.longitude
      }

      // Selected vehicle is drawn noticeably bigger and above the others
      const isSelected = selectedVehicle?.id === vehicle.id
      const rel = isSelected ? 1.4 : 1
      const markerSize = vehicleMarkerSize(isSelected)
      const existing = markersRef.current.get(vehicle.id)
      if (existing) {
        const meta = (existing as any).__markerMeta || {}
        const from = (existing as any).__displayedPosition || meta.position || position
        existing.content = createVehicleMarkerContent(vehicle, markerSize, isSelected)
        existing.title = vehicle.name
        existing.zIndex = isSelected ? 3000 : 500
        meta.vehicle = vehicle
        meta.rel = rel
        meta.position = position
        ;(existing as any).__markerMeta = meta
        animateVehicleMarker(
          existing,
          vehicle.id,
          from,
          position,
          meta.previousUpdate,
          vehicle.lastUpdate,
          vehicle.trackingStatus === 'LIVE'
        )
        meta.previousUpdate = vehicle.lastUpdate
        return
      }

      const marker = new google.maps.marker.AdvancedMarkerElement({
        position,
        map: mapInstanceRef.current,
        title: vehicle.name,
        content: createVehicleMarkerContent(vehicle, markerSize, isSelected),
        zIndex: isSelected ? 3000 : 500,
        gmpClickable: true
      })
      ;(marker as any).__displayedPosition = position

      // Create info window
      const infoWindow = new google.maps.InfoWindow()
      ;(marker as any).__markerMeta = {
        type: 'vehicle', vehicle, rel, position,
        previousUpdate: vehicle.lastUpdate, infoWindow
      }

      // Add click listeners
      marker.addListener('click', () => {
        const current = (marker as any).__markerMeta?.vehicle as Vehicle
        infoWindow.setContent(vehicleInfoHtml(current))
        onVehicleSelect(current)
        infoWindow.open({ map: mapInstanceRef.current, anchor: marker })
      })

      // Store marker reference
      markersRef.current.set(vehicle.id, marker)
      } catch (error) {
        // One unusable vehicle must not leave the whole map without pucks.
        console.error(`Could not place the marker for ${vehicle.id}:`, error)
      }
    })

    // Frame the visible fleet (capped so a single vehicle doesn't zoom to street level)
    if (fitToVehicles && shownVehicles.length > 0) {
      const bounds = new google.maps.LatLngBounds()
      shownVehicles.forEach(v => bounds.extend({ lat: v.location.latitude, lng: v.location.longitude }))
      google.maps.event.addListenerOnce(mapInstanceRef.current, 'bounds_changed', () => {
        const zoom = mapInstanceRef.current?.getZoom()
        if (zoom != null && zoom > 15) mapInstanceRef.current?.setZoom(15)
      })
      mapInstanceRef.current.fitBounds(bounds, 60)
    }

    // Set up global functions used by the Google Maps InfoWindow HTML.
    ;(window as any).showTrajectory = (vehicleId: string) => {
      loadVehicleTrajectory(vehicleId)
    }
    ;(window as any).createAlertForVehicle = (vehicleId: string) => {
      const vehicle = vehicles.find(v => v.id === vehicleId)
      if (vehicle && onCreateAlert) onCreateAlert(vehicle)
    }
    ;(window as any).createFromVehiclePosition = (vehicleId: string, mode: PositionCreationMode) => {
      const vehicle = vehicles.find(v => v.id === vehicleId)
      if (!vehicle || !vehicleHasValidPosition(vehicle) || !onCreateFromPosition) return
      onCreateFromPosition({
        mode,
        point: { lat: vehicle.location.latitude, lng: vehicle.location.longitude },
        zoom: mapInstanceRef.current?.getZoom() || 15,
        referenceTrajectory:
          activeTrajectoryVehicleRef.current === vehicleId
            ? (trajectoryDataRef.current?.optimizedPoints || []).map(point => ({ lat: point.lat, lng: point.lng }))
            : [],
        dateRange: { ...dateRange },
        vehicle
      })
    }
  }

  // Create color-coded trajectory segments based on speed and movement state
  const createColorCodedTrajectory = (validPoints: any[], optimizedPoints: any[]) => {
    const segments: google.maps.Polyline[] = []
    
    // Function to calculate stroke weight based on zoom level
    const getStrokeWeight = (baseWeight: number) => {
      if (!mapInstanceRef.current) return baseWeight
      
      const zoom = mapInstanceRef.current.getZoom() || 10
      // Zoom levels typically range from 1 (world view) to 20+ (street level)
      // Scale the stroke weight: thicker when zoomed in, thinner when zoomed out
      const zoomFactor = Math.max(0.5, Math.min(3, zoom / 10)) // Factor between 0.5 and 3
      return Math.round(baseWeight * zoomFactor)
    }
    
    // Function to get color based on speed and movement state
    const getTrajectoryColor = (speed: number, isStationary: boolean, duration?: number) => {
      if (isStationary) {
        // Red for stops along the road
        return {
          color: '#EF4444', // Red
          opacity: 0.9,
          weight: 6,
          description: 'Stopped'
        }
      } else if (speed === 0 || speed < 1) {
        // Dark red for very slow movement
        return {
          color: '#DC2626', // Dark red
          opacity: 0.8,
          weight: 5,
          description: 'Very slow'
        }
      } else if (speed < 20) {
        // Orange for slow movement
        return {
          color: '#F59E0B', // Orange
          opacity: 0.8,
          weight: 4,
          description: 'Slow movement'
        }
      } else if (speed < 50) {
        // Yellow-green for normal movement
        return {
          color: '#84CC16', // Lime
          opacity: 0.8,
          weight: 4,
          description: 'Normal speed'
        }
      } else if (speed < 80) {
        // Green for good movement
        return {
          color: '#10B981', // Green
          opacity: 0.8,
          weight: 4,
          description: 'Good speed'
        }
      } else {
        // Blue for high speed
        return {
          color: '#3B82F6', // Blue
          opacity: 0.8,
          weight: 4,
          description: 'High speed'
        }
      }
    }

    // Detect stop events for color coding
    const stopEvents = detectStopEvents(validPoints)

    // Create segments between consecutive points
    for (let i = 0; i < optimizedPoints.length - 1; i++) {
      const currentPoint = optimizedPoints[i]
      const nextPoint = optimizedPoints[i + 1]
      if (nextPoint.breakBefore) continue
      
      // Find corresponding speed data
      const currentData = validPoints.find(p => 
        Math.abs(p.lat - currentPoint.lat) < 0.0001 && 
        Math.abs(p.lng - currentPoint.lng) < 0.0001
      )
      
      const speed = Number(currentPoint.speed ?? currentData?.speed ?? 0)
      const isAtStop = stopEvents.some(stop =>
        calculateDistance(currentPoint.lat, currentPoint.lng, stop.latitude, stop.longitude) < 0.03)
      
      const colorInfo = getTrajectoryColor(speed, isAtStop)
      
      // Create segment polyline with dynamic stroke weight based on zoom
      const dynamicWeight = getStrokeWeight(colorInfo.weight)
      const segment = new google.maps.Polyline({
        path: [currentPoint, nextPoint],
        geodesic: true,
        strokeColor: colorInfo.color,
        strokeOpacity: colorInfo.opacity,
        strokeWeight: dynamicWeight,
        zIndex: isAtStop ? 1000 : 500 // Higher z-index for stops
      })

      // Add click listener for segment info
      segment.addListener('click', (event: any) => {
        const infoWindow = new google.maps.InfoWindow({
          position: event.latLng,
          content: `
            <div class="p-2 text-sm">
              <div class="font-medium mb-1">${colorInfo.description}</div>
              <div>Speed: ${speed.toFixed(1)} km/h</div>
              ${currentPoint.timestamp ? `<div>Time: ${new Date(currentPoint.timestamp).toLocaleTimeString()}</div>` : ''}
            </div>
          `
        })
        infoWindow.open(mapInstanceRef.current)
      })

      segments.push(segment)
    }

    console.log(`🎨 Created ${segments.length} color-coded trajectory segments`)
    return segments
  }

  // Update trajectory line width based on zoom level
  const updateTrajectoryWidthOnZoom = () => {
    if (!mapInstanceRef.current || !trajectorySegmentsRef.current.length || !trajectoryDataRef.current) return

    const zoom = mapInstanceRef.current.getZoom() || 10
    console.log(`🔍 Zoom changed to: ${zoom}, updating trajectory width`)

    // Recreate trajectory segments with new zoom-based widths
    const { validPoints, optimizedPoints } = trajectoryDataRef.current
    const newSegments = createColorCodedTrajectory(validPoints, optimizedPoints)
    
    // Remove old segments
    trajectorySegmentsRef.current.forEach(segment => segment.setMap(null))
    
    // Add new segments with updated widths
    newSegments.forEach(segment => segment.setMap(mapInstanceRef.current))
    trajectorySegmentsRef.current = newSegments
  }

  // Add trajectory color legend to the map
  const addTrajectoryLegend = () => {
    if (!mapInstanceRef.current) return

    // Remove existing legend
    const existingLegend = document.getElementById('trajectory-legend')
    if (existingLegend) {
      existingLegend.remove()
    }

    // Only show legend if showLegend is true
    if (!showLegend) return

    // Create legend HTML
    const legendDiv = document.createElement('div')
    legendDiv.id = 'trajectory-legend'
    legendDiv.innerHTML = `
      <div style="
        background: white;
        border: 2px solid #ccc;
        border-radius: 8px;
        padding: 10px;
        margin: 10px;
        box-shadow: 0 2px 10px rgba(0,0,0,0.2);
        font-family: Arial, sans-serif;
        font-size: 12px;
        line-height: 1.4;
      ">
        <div style="font-weight: bold; margin-bottom: 8px; color: #333;">Speed Legend</div>
        <div style="display: flex; flex-direction: column; gap: 4px;">
          <div style="display: flex; align-items: center; gap: 8px;">
            <div style="width: 20px; height: 4px; background: #EF4444; border-radius: 2px;"></div>
            <span>Stopped</span>
          </div>
          <div style="display: flex; align-items: center; gap: 8px;">
            <div style="width: 20px; height: 4px; background: #DC2626; border-radius: 2px;"></div>
            <span>Very slow (&lt;1 km/h)</span>
          </div>
          <div style="display: flex; align-items: center; gap: 8px;">
            <div style="width: 20px; height: 4px; background: #F59E0B; border-radius: 2px;"></div>
            <span>Slow (1-20 km/h)</span>
          </div>
          <div style="display: flex; align-items: center; gap: 8px;">
            <div style="width: 20px; height: 4px; background: #84CC16; border-radius: 2px;"></div>
            <span>Normal (20-50 km/h)</span>
          </div>
          <div style="display: flex; align-items: center; gap: 8px;">
            <div style="width: 20px; height: 4px; background: #10B981; border-radius: 2px;"></div>
            <span>Good (50-80 km/h)</span>
          </div>
          <div style="display: flex; align-items: center; gap: 8px;">
            <div style="width: 20px; height: 4px; background: #3B82F6; border-radius: 2px;"></div>
            <span>High (&gt;80 km/h)</span>
          </div>
        </div>
        <div style="font-weight: bold; margin: 10px 0 6px; color: #333;">${escapeHtml(t('trajectory_markers', { defaultValue: 'Markers' }))}</div>
        <div style="display: flex; flex-direction: column; gap: 4px;">
          <div style="display: flex; align-items: center; gap: 8px;">
            <div style="width: 12px; height: 12px; background: #16A34A; border-radius: 50%; border: 2px solid #fff; box-shadow: 0 0 0 1px #ccc;"></div>
            <span>${escapeHtml(t('departure', { defaultValue: 'Departure' }))}</span>
          </div>
          <div style="display: flex; align-items: center; gap: 8px;">
            <div style="width: 12px; height: 12px; background: #DC2626; border-radius: 50%; border: 2px solid #fff; box-shadow: 0 0 0 1px #ccc;"></div>
            <span>${escapeHtml(t('journey_end', { defaultValue: 'Arrival / end of period' }))}</span>
          </div>
          <label title="${escapeHtml(t('toggle_device_events_hint', { defaultValue: 'Show or hide vehicle events on the route' }))}" style="display: flex; align-items: center; gap: 8px; cursor:pointer;">
            <input data-marker-kind="event" type="checkbox" ${visibleMarkerKinds.event ? 'checked' : ''} />
            <div style="width: 12px; height: 12px; background: #2563EB; border-radius: 50%; border: 2px solid #fff; box-shadow: 0 0 0 1px #ccc;"></div>
            <span>${escapeHtml(t('device_event', { defaultValue: 'Vehicle event' }))}</span>
          </label>
          <label title="${escapeHtml(t('toggle_device_alerts_hint', { defaultValue: 'Show or hide vehicle alerts and triggered rules on the route' }))}" style="display: flex; align-items: center; gap: 8px; cursor:pointer;">
            <input data-marker-kind="alert" type="checkbox" ${visibleMarkerKinds.alert ? 'checked' : ''} />
            <div style="width: 12px; height: 12px; background: #DC2626; border-radius: 50%; border: 2px solid #fff; box-shadow: 0 0 0 1px #ccc;"></div>
            <span>${escapeHtml(t('device_alert', { defaultValue: 'Vehicle alert' }))}</span>
          </label>
          <label title="${escapeHtml(t('toggle_stops_hint', { defaultValue: 'Show or hide stops on the route' }))}" style="display: flex; align-items: center; gap: 8px; cursor:pointer;">
            <input data-marker-kind="stop" type="checkbox" ${visibleMarkerKinds.stop ? 'checked' : ''} />
            <div style="width: 12px; height: 12px; background: #F59E0B; border-radius: 50%; border: 2px solid #fff; box-shadow: 0 0 0 1px #ccc;"></div>
            <span>${escapeHtml(t('stop', { defaultValue: 'Stop' }))}</span>
          </label>
          <label title="${escapeHtml(t('toggle_parking_hint', { defaultValue: 'Show or hide long parking events on the route' }))}" style="display: flex; align-items: center; gap: 8px; cursor:pointer;">
            <input data-marker-kind="parking" type="checkbox" ${visibleMarkerKinds.parking ? 'checked' : ''} />
            <div style="width: 12px; height: 12px; background: #7C3AED; border-radius: 50%; border: 2px solid #fff; box-shadow: 0 0 0 1px #ccc;"></div>
            <span>${escapeHtml(t('parking_5h', { defaultValue: 'Parking (5h+)' }))}</span>
          </label>
          <div style="display: flex; align-items: center; gap: 8px;">
            <img src="/assets/van.svg" style="width: 14px; height: 14px;" alt=""/>
            <span>${escapeHtml(t('current_position', { defaultValue: 'Current position' }))}</span>
          </div>
        </div>
        <div style="font-weight: bold; margin: 10px 0 6px; color: #333;">${escapeHtml(t('vehicle_pucks', { defaultValue: 'Vehicle pucks' }))}</div>
        <div style="display: flex; flex-direction: column; gap: 4px;">
          ${(['LIVE-MOVING', 'LIVE-PARKED', 'STALE', 'NOT_TRACKED'] as const).map(state => {
            const presentation = state === 'LIVE-MOVING'
              ? trackingPresentation('LIVE', 30)
              : state === 'LIVE-PARKED' ? trackingPresentation('LIVE', 0) : trackingPresentation(state)
            return `<div style="display: flex; align-items: center; gap: 8px;">
              <div style="width: 12px; height: 12px; background: ${presentation.color}; border-radius: 50%; border: 2px solid #fff; box-shadow: 0 0 0 1px #ccc;"></div>
              <span>${escapeHtml(t(presentation.labelKey, { defaultValue: presentation.label }))}</span>
            </div>`
          }).join('')}
        </div>
        <div style="margin-top: 8px; font-size: 10px; color: #666; font-style: italic;">
          Click on trajectory segments for details
        </div>
      </div>
    `

    legendDiv.querySelectorAll<HTMLInputElement>('input[data-marker-kind]').forEach(input => {
      input.addEventListener('change', () => {
        const kind = input.dataset.markerKind as 'event' | 'alert' | 'stop' | 'parking'
        setVisibleMarkerKinds(previous => ({ ...previous, [kind]: input.checked }))
      })
    })

    // Add legend to map
    mapInstanceRef.current.controls[google.maps.ControlPosition.LEFT_BOTTOM].push(legendDiv)
  }

  // Add legend toggle button to the map
  const addLegendToggleButton = () => {
    if (!mapInstanceRef.current) return

    // Remove existing button
    const existingButton = document.getElementById('legend-toggle-button')
    if (existingButton) {
      existingButton.remove()
    }

    // Create toggle switch HTML
    const buttonDiv = document.createElement('div')
    buttonDiv.id = 'legend-toggle-button'
    
    // Function to update button appearance
    const updateButtonAppearance = (isOn: boolean) => {
      buttonDiv.innerHTML = `
        <div id="legend-toggle-container" style="
          background: white;
          border: 2px solid #ccc;
          border-radius: 20px;
          padding: 8px 12px;
          margin: 10px;
          box-shadow: 0 2px 6px rgba(0,0,0,0.2);
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 10px;
          transition: all 0.2s ease;
          user-select: none;
        " onmouseover="this.style.background='#f5f5f5'" onmouseout="this.style.background='white'">
          <span style="font-size: 12px; font-weight: 600; color: #333;">Legend</span>
          <div id="legend-toggle-switch" style="
            position: relative;
            width: 40px;
            height: 20px;
            background: ${isOn ? '#4CAF50' : '#ccc'};
            border-radius: 10px;
            transition: all 0.3s ease;
            cursor: pointer;
          ">
            <div id="legend-toggle-circle" style="
              position: absolute;
              top: 2px;
              left: ${isOn ? '22px' : '2px'};
              width: 16px;
              height: 16px;
              background: white;
              border-radius: 50%;
              transition: all 0.3s ease;
              box-shadow: 0 1px 3px rgba(0,0,0,0.3);
            "></div>
          </div>
        </div>
      `
    }

    // Initial appearance
    updateButtonAppearance(showLegend)

    // Add click event listener with proper state management
    buttonDiv.addEventListener('click', (e) => {
      e.preventDefault()
      e.stopPropagation()
      
      console.log(`🎨 Legend toggle clicked! Current showLegend: ${showLegend}`)
      
      setShowLegend(prev => {
        const newState = !prev
        console.log(`🔄 State changing from ${prev} to ${newState}`)
        
        // Update button appearance immediately
        setTimeout(() => updateButtonAppearance(newState), 0)
        
        return newState
      })
    })

    // Add button to map (positioned at top left)
    mapInstanceRef.current.controls[google.maps.ControlPosition.TOP_LEFT].push(buttonDiv)
    
    console.log('🎨 Legend toggle button added to map')
  }

  // Update existing legend toggle button appearance
  const updateLegendToggleButton = () => {
    // Since we're recreating the button content on each click,
    // this function is mainly for the useEffect that responds to showLegend changes
    const existingButton = document.getElementById('legend-toggle-button')
    if (existingButton) {
      // Trigger a re-render of the button by calling addLegendToggleButton
      // But only if the button exists (to avoid infinite loops)
      console.log(`🎨 Legend state changed to: ${showLegend ? 'ON' : 'OFF'}`)
    }
  }

  const markerKindLabel = (kind: BadgeKind) => {
    const labels: Record<BadgeKind, string> = {
      start: t('departure', { defaultValue: 'Departure' }),
      end: t('journey_end', { defaultValue: 'Arrival / end of period' }),
      event: t('device_event', { defaultValue: 'Vehicle event' }),
      alert: t('device_alert', { defaultValue: 'Vehicle alert' }),
      stop: t('stop', { defaultValue: 'Stop' }),
      parking: t('parking', { defaultValue: 'Parking' })
    }
    return labels[kind]
  }

  const markerShouldBeVisible = (kind: BadgeKind) => {
    if (kind === 'start' || kind === 'end') return true
    return visibleMarkerKinds[kind]
  }

  const addMarkerHover = (
    marker: google.maps.Marker,
    kind: BadgeKind,
    hoverHtml: string,
    baseSize: number,
    count = 1
  ) => {
    const hoverWindow = new google.maps.InfoWindow({
      content: `<div style="padding:4px 6px;font-size:12px;white-space:nowrap"><strong>${escapeHtml(markerKindLabel(kind))}</strong>${hoverHtml ? `<br/>${hoverHtml}` : ''}</div>`,
      disableAutoPan: true
    })
    marker.addListener('mouseover', () => {
      marker.setIcon(lucideBadgeIcon(kind, baseSize + 4, count))
      hoverWindow.open(mapInstanceRef.current, marker)
    })
    marker.addListener('mouseout', () => {
      marker.setIcon(lucideBadgeIcon(kind, baseSize, count))
      hoverWindow.close()
    })
  }

  const addTrajectoryEventMarkers = (vehicle: Vehicle, trajectoryPoints: any[]) => {
    if (!mapInstanceRef.current || !vehicle) return
    eventMarkersRef.current.forEach(marker => marker.setMap(null))
    eventMarkersRef.current = []
    // The timeline contains both GPS/IO samples used to draw the route and
    // real activities. Only the latter deserve a sticker on the map.
    // Use an allow-list so a new vendor telemetry code cannot unexpectedly
    // cover the trajectory with thousands of markers (for example IO_385).
    const meaningfulEventPattern =
      /(ALERT|ALARM|SOS|PANIC|TAMPER|INCIDENT|CRASH|COLLISION|GEOFENCE|OVERSPEED|SPEEDING|IGNITION_(ON|OFF)|ENGINE_(START|STOP|ON|OFF)|MOVEMENT_(START|STOP)|DOOR_(OPEN|CLOSE)|LOCK|UNLOCK|THEFT|POWER_(CUT|OFF|ON)|LOW_BATTERY|FUEL)/
    const zoom = mapInstanceRef.current.getZoom() || 15
    const groupingRadiusKm = zoom <= 10 ? 0.5 : zoom <= 12 ? 0.15 : zoom <= 14 ? 0.05 : 0.02
    type EventGroup = { kind: 'event' | 'alert', points: any[] }
    const groups: EventGroup[] = []
    // Teltonika units flip ignition off and on again within seconds while
    // the engine never stopped. Such a pair is noise, not two engine events.
    const ignitionFlicker = new Set<any>()
    const ignitionEvents = trajectoryPoints.filter(point => /IGNITION_(ON|OFF)|ENGINE_(START|STOP|ON|OFF)/.test(String(point.event || '').toUpperCase()))
    ignitionEvents.forEach((point, index) => {
      const next = ignitionEvents[index + 1]
      if (!next) return
      const off = /OFF|STOP/.test(String(point.event).toUpperCase())
      const backOn = /ON|START/.test(String(next.event).toUpperCase()) && !/OFF|STOP/.test(String(next.event).toUpperCase())
      const seconds = (new Date(next.timestamp).getTime() - new Date(point.timestamp).getTime()) / 1000
      if (off && backOn && seconds >= 0 && seconds <= 60) {
        ignitionFlicker.add(point)
        ignitionFlicker.add(next)
      }
    })
    trajectoryPoints.forEach(point => {
      if (ignitionFlicker.has(point)) return
      const type = String(point.event || '').toUpperCase()
      const recordType = String(point.recordType || '').toUpperCase()
      if (!type || (recordType !== 'ALERT' && !meaningfulEventPattern.test(type))) return
      const kind: 'event' | 'alert' =
        recordType === 'ALERT' || /(ALERT|ALARM|SOS|PANIC|TAMPER|INCIDENT|CRASH|COLLISION|THEFT|OVERSPEED|SPEEDING|POWER_CUT|LOW_BATTERY)/.test(type)
          ? 'alert'
          : 'event'
      const nearby = groups.find(group =>
        group.kind === kind &&
        calculateDistance(group.points[0].lat, group.points[0].lng, point.lat, point.lng) <= groupingRadiusKm
      )
      if (nearby) nearby.points.push(point)
      else groups.push({ kind, points: [point] })
    })

    groups.forEach((group, index) => {
      const point = group.points[0]
      const kind = group.kind
      const count = group.points.length
      const types = [...new Set(group.points.map(item => String(item.title || item.event).toUpperCase()))]
      const title = count > 1
        ? `${markerKindLabel(kind)} (${count})`
        : String(point.title || types[0]).replace(/_/g, ' ')
      const size = kind === 'alert' ? 28 : 26
      const marker = new google.maps.Marker({
        position: { lat: point.lat, lng: point.lng },
        map: markerShouldBeVisible(kind) ? mapInstanceRef.current : null,
        icon: lucideBadgeIcon(kind, size, count),
        title,
        zIndex: 900 + index
      })
      ;(marker as any).__markerMeta = { type: 'badge', kind, count }
      eventMarkersRef.current.push(marker)

      const vehicleName = vehicle.name || ''
      const vehicleNumber = vehicle.operationalIdentifier || vehicle.plateNumber || ''
      const primaryLabel = humanEventLabel(String(point.title || point.event || title))
      const primaryDetail = usefulEventDetail(point.description, primaryLabel)
      const primaryStatus = humanStatus(point.status)
      const extraNotes = group.points.slice(1, 8).map(item => {
        const label = humanEventLabel(String(item.title || item.event || ''))
        const when = formatStopInstant(new Date(item.timestamp))
        const speed = `${Number(item.speed || 0).toFixed(0)} km/h`
        const detail = usefulEventDetail(item.description, label)
        return [label, when, speed, detail || ''].filter(Boolean).join(' · ')
      })
      if (count > 8) extraNotes.push(`+${count - 8} ${t('more_events', { defaultValue: 'more events' })}`)
      const eventCard = buildMapCard({
        tone: kind === 'alert' ? 'red' : 'blue',
        glyph: kind === 'alert' ? 'alert' : 'event',
        title: count > 1 ? `${primaryLabel} · ${count}` : primaryLabel,
        aside: `${Number(point.speed || 0).toFixed(0)} km/h`,
        vehicleName,
        vehicleNumber,
        rows: [['Time', formatStopInstant(new Date(point.timestamp))]],
        latitude: point.lat,
        longitude: point.lng,
        detail: primaryDetail,
        notes: extraNotes,
        pills: primaryStatus ? [primaryStatus] : []
      })
      const infoWindow = new google.maps.InfoWindow({ content: eventCard.element, maxWidth: 320 })
      addMarkerHover(
        marker,
        kind,
        count > 1
          ? `${count} ${escapeHtml(t('events_at_this_position', { defaultValue: 'events at this position' }))}`
          : `${escapeHtml(String(point.title || types[0]).replace(/_/g, ' '))} · ${escapeHtml(new Date(point.timestamp).toLocaleTimeString())}`,
        size,
        count
      )
      marker.addListener('click', () => {
        infoWindow.open(mapInstanceRef.current, marker)
        revealPlace(eventCard.placeLine, eventCard.coordsLine, point.lat, point.lng)
      })
    })
  }

  const loadVehicleTrajectory = async (
    vehicleId: string,
    abortSignal?: AbortSignal,
    preserveTimelineMarker = false
  ) => {
    if (!mapInstanceRef.current) return

    // Track which vehicle's trajectory we're loading
    activeTrajectoryVehicleRef.current = vehicleId

    try {
      // Extract actual vehicle ID and type if vehicleId is in ASSET-123 or CONTAINER-123 format
      let actualVehicleId = vehicleId
      let vehicleType = undefined
      
      if (vehicleId.startsWith('ASSET-')) {
        actualVehicleId = vehicleId.replace('ASSET-', '')
        vehicleType = 'asset'
      } else if (vehicleId.startsWith('CONTAINER-')) {
        actualVehicleId = vehicleId.replace('CONTAINER-', '')
        vehicleType = 'container'
      } else if (selectedVehicle) {
        // Use selectedVehicle to determine type
        const isAsset = selectedVehicle.assetInfo !== undefined
        const isContainer = selectedVehicle.containerInfo !== undefined
        actualVehicleId = isAsset ? String(selectedVehicle.assetInfo?.id) : isContainer ? String(selectedVehicle.containerInfo?.id) : vehicleId
        vehicleType = isAsset ? 'asset' : isContainer ? 'container' : undefined
      }
      
      // A rolling window carries exact instants; whole-day presets do not.
      const from = dateRange.startAt || dateRange.startDate
      const to = dateRange.endAt || dateRange.endDate
      console.log(`🛣️ Loading trajectory for ${vehicleType || 'vehicle'}: ${actualVehicleId} from ${from} to ${to}`)

      // Calculate hours from date range (no artificial limit)
      const startTime = new Date(from).getTime()
      const endTime = new Date(to).getTime()
      const hours = Math.ceil((endTime - startTime) / (1000 * 60 * 60))
      
      console.log(`⏰ Requesting ${hours} hours of data for full date range`)
      
      const response = await apiClient.get(`/timeline/${actualVehicleId}`, { 
        startDate: from,
        endDate: to,
        hours: hours,
        vehicleType: vehicleType
      }, {
        signal: abortSignal // Pass signal in axios config, not as query param
      })

      // After async: verify this vehicle is still the one we want to display
      if (activeTrajectoryVehicleRef.current !== vehicleId) {
        console.log(`⏭️ Skipping stale trajectory response for ${vehicleId}, active is now ${activeTrajectoryVehicleRef.current}`)
        return
      }
      
      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        const trajectory = response.data
        console.log(`✅ Trajectory loaded: ${trajectory.length} points for date range ${dateRange.startDate} to ${dateRange.endDate}`)

        // Ensure we're only showing trajectory for the selected vehicle
        console.log(`🎯 Loading trajectory ONLY for selected vehicle: ${vehicleId}`)
        
        // Clear all existing trajectories to prevent overlap
        clearAllTrajectories(preserveTimelineMarker)
        activeTrajectoryVehicleRef.current = vehicleId

        // Filter and validate points (API now handles date filtering)
        const validPoints = trajectory
          .filter((point: any) => 
            point.location?.latitude && 
            point.location?.longitude && 
            !isNaN(point.location.latitude) && 
            !isNaN(point.location.longitude) &&
            point.timestamp
          )
          .map((point: any) => ({
            lat: parseFloat(point.location.latitude),
            lng: parseFloat(point.location.longitude),
            speed: parseFloat(point.speed || 0),
            timestamp: new Date(point.timestamp),
            event: point.event,
            title: point.title,
            description: point.description,
            severity: point.severity,
            status: point.status,
            recordType: point.recordType,
            batteryLevel: point.batteryLevel,
            signalStrength: point.signalStrength
          }))
          .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime()) // Sort by time

        if (validPoints.length === 0) {
          console.warn(`⚠️ No valid trajectory points found for date range ${dateRange.startDate} to ${dateRange.endDate}`)
          return
        }
        
        console.log(`📅 Filtered trajectory points by date range: ${validPoints.length} points from ${dateRange.startDate} to ${dateRange.endDate}`)
        console.log(`🎯 Showing trajectory ONLY for vehicle: ${vehicleId} (${selectedVehicle?.name})`)

        // Detect stop events in trajectory
        const stopEvents = detectStopEvents(validPoints)
        console.log(`🛑 Detected ${stopEvents.length} stop events`)

        // Apply trajectory smoothing and optimization
        const smoothedPoints = smoothTrajectory(validPoints)
        const optimizedPoints = optimizeTrajectoryPoints(smoothedPoints, 500)
        
        console.log(`🎯 Optimized trajectory: ${optimizedPoints.length} points`)

        // Store trajectory data for zoom updates
        trajectoryDataRef.current = { validPoints, optimizedPoints }

        // Create color-coded trajectory segments based on speed and movement
        const trajectorySegments = createColorCodedTrajectory(validPoints, optimizedPoints)
        
        // Store all trajectory segments for cleanup
        trajectoryRef.current = {
          setMap: (map: google.maps.Map | null) => {
            trajectorySegments.forEach(segment => segment.setMap(map))
          }
        } as any

        // Add all segments to map
        trajectorySegments.forEach(segment => segment.setMap(mapInstanceRef.current))

        // Store trajectory segments for zoom updates
        trajectorySegmentsRef.current = trajectorySegments

        // Add zoom change listener to update trajectory width
        const zoomListener = mapInstanceRef.current.addListener('zoom_changed', () => {
          updateTrajectoryWidthOnZoom()
          if (selectedVehicle) addTrajectoryEventMarkers(selectedVehicle, validPoints)
          reclusterRouteBadges()
        })

        // Store listener for cleanup
        if (!mapListenersRef.current) {
          mapListenersRef.current = []
        }
        mapListenersRef.current.push(zoomListener)

        // Add trajectory legend
        addTrajectoryLegend()

        // Add legend toggle button
        addLegendToggleButton()

        // Plot only meaningful vehicle events; no GPS/IO checkpoint markers.
        addTrajectoryEventMarkers(selectedVehicle, validPoints)

        // Add start and end markers
        if (optimizedPoints.length > 1) {
          // Start marker: Lucide flag badge (green)
          const startRel = 0.75
          const startMarker = new google.maps.Marker({
            position: optimizedPoints[0],
            map: mapInstanceRef.current,
            icon: lucideBadgeIcon('start', 27),
            title: `${t('departure', { defaultValue: 'Departure' })} - ${validPoints[0].timestamp.toLocaleString()}`,
            zIndex: 1500
          })
          ;(startMarker as any).__markerMeta = { type: 'badge', kind: 'start', rel: startRel }

          // Start marker info window
          const endpointVehicleName = selectedVehicle?.name || ''
          const endpointVehicleNumber = selectedVehicle?.operationalIdentifier || selectedVehicle?.plateNumber || ''
          const startCard = buildMapCard({
            tone: 'green',
            glyph: 'flag',
            title: t('departure', { defaultValue: 'Departure' }),
            aside: `${Number(validPoints[0].speed || 0).toFixed(0)} km/h`,
            vehicleName: endpointVehicleName,
            vehicleNumber: endpointVehicleNumber,
            rows: [['Time', formatStopInstant(validPoints[0].timestamp)]],
            latitude: optimizedPoints[0].lat,
            longitude: optimizedPoints[0].lng
          })
          const startInfoWindow = new google.maps.InfoWindow({ content: startCard.element, maxWidth: 320 })

          startMarker.addListener('click', () => {
            startInfoWindow.open(mapInstanceRef.current, startMarker)
            revealPlace(startCard.placeLine, startCard.coordsLine, optimizedPoints[0].lat, optimizedPoints[0].lng)
          })
          trajectoryMarkersRef.current.push(startMarker)

          // The red endpoint is the last recorded position inside the selected
          // period. It is intentionally called "arrival / end of period", not
          // a planned destination, because the timeline contains observed GPS.
          const lastPoint = optimizedPoints[optimizedPoints.length - 1]
          const lastData = validPoints[validPoints.length - 1]
          const endLabel = t('journey_end', { defaultValue: 'Arrival / end of period' })
          const endMarker = new google.maps.Marker({
            position: lastPoint,
            map: mapInstanceRef.current,
            icon: lucideBadgeIcon('end', 27),
            title: `${endLabel} - ${lastData.timestamp.toLocaleString()}`,
            zIndex: 1501
          })
          ;(endMarker as any).__markerMeta = { type: 'badge', kind: 'end', rel: 0.75 }
          const endCard = buildMapCard({
            tone: 'red',
            glyph: 'end',
            title: endLabel,
            aside: `${Number(lastData.speed || 0).toFixed(0)} km/h`,
            vehicleName: endpointVehicleName,
            vehicleNumber: endpointVehicleNumber,
            rows: [['Time', formatStopInstant(lastData.timestamp)]],
            latitude: lastPoint.lat,
            longitude: lastPoint.lng
          })
          const endInfoWindow = new google.maps.InfoWindow({ content: endCard.element, maxWidth: 320 })
          endMarker.addListener('click', () => {
            endInfoWindow.open(mapInstanceRef.current, endMarker)
            revealPlace(endCard.placeLine, endCard.coordsLine, lastPoint.lat, lastPoint.lng)
          })
          trajectoryMarkersRef.current.push(endMarker)
        }

        // Add stop event markers (Lucide badges: parking = P, stop = pause)
        stopEvents.forEach((stopEvent, index) => {
          const isLongParking = stopEvent.durationHours >= 5
          const stopType = isLongParking ? 'Parking' : 'Stop'
          const badgeKind: 'parking' | 'stop' = isLongParking ? 'parking' : 'stop'
          const stopRel = isLongParking ? 0.85 : 0.75

          const vehicleName = selectedVehicle?.name || ''
          const vehicleNumber = selectedVehicle?.operationalIdentifier || selectedVehicle?.plateNumber || ''
          const stopMarker = new google.maps.Marker({
            position: { lat: stopEvent.latitude, lng: stopEvent.longitude },
            map: visibleMarkerKinds[badgeKind] ? mapInstanceRef.current : null,
            icon: lucideBadgeIcon(badgeKind, isLongParking ? 30 : 28),
            title: [vehicleName, `${stopType} #${index + 1}`].filter(Boolean).join(' · '),
            zIndex: 1000 // Ensure stop markers appear above trajectory
          })
          ;(stopMarker as any).__markerMeta = { type: 'badge', kind: badgeKind, rel: stopRel, count: 1 }

          const { element: stopCard, placeLine, coordsLine } = buildStopCard({
            index: index + 1,
            isLongParking,
            durationLabel: formatDuration(stopEvent.durationHours),
            startLabel: formatStopInstant(stopEvent.startTime),
            endLabel: formatStopInstant(stopEvent.endTime),
            latitude: stopEvent.latitude,
            longitude: stopEvent.longitude,
            vehicleName,
            vehicleNumber,
            batteryLevel: stopEvent.batteryLevel,
            signalStrength: stopEvent.signalStrength
          })
          const stopInfoWindow = new google.maps.InfoWindow({ content: stopCard, maxWidth: 320 })

          stopMarker.addListener('click', () => {
            stopInfoWindow.open(mapInstanceRef.current, stopMarker)
            revealPlace(placeLine, coordsLine, stopEvent.latitude, stopEvent.longitude)
          })
          addMarkerHover(
            stopMarker,
            badgeKind,
            [vehicleName, vehicleNumber !== vehicleName ? vehicleNumber : '', formatDuration(stopEvent.durationHours)]
              .filter(Boolean).join(' · '),
            isLongParking ? 30 : 28
          )
          trajectoryMarkersRef.current.push(stopMarker)
        })
        reclusterRouteBadges()

        // Fit map to trajectory bounds — but ONLY for user-initiated loads
        // (selection, date change). Live refreshes (preserveTimelineMarker)
        // must never yank the user's zoom/position.
        if (!preserveTimelineMarker) {
          const bounds = new google.maps.LatLngBounds()
          optimizedPoints.forEach(point => bounds.extend(point))
          stopEvents.forEach(stop => bounds.extend({ lat: stop.latitude, lng: stop.longitude }))
          mapInstanceRef.current.fitBounds(bounds)
        }

        // Calculate trajectory statistics
        const stats = calculateTrajectoryStats(validPoints, stopEvents)
        setTrajectoryStats(stats)

        // Show summary of stop events
        console.log(`📊 Stop Events Summary:`)
        console.log(`   • Short stops (<5h): ${stopEvents.filter(s => s.durationHours < 5).length}`)
        console.log(`   • Long parking (≥5h): ${stopEvents.filter(s => s.durationHours >= 5).length}`)
        console.log(`📊 Journey Stats:`)
        console.log(`   • Total Distance: ${stats.totalDistance.toFixed(2)} km`)
        console.log(`   • Total Duration: ${Math.round(stats.totalDuration)} minutes`)
        console.log(`   • Max Speed: ${stats.maxSpeed} km/h`)
        console.log(`   • Avg Speed: ${stats.avgSpeed.toFixed(1)} km/h`)

      } else {
        console.warn('⚠️ No trajectory data available')
      }
    } catch (error) {
      // Only log non-abort errors
      if (error.name !== 'AbortError') {
        console.error('❌ Error loading trajectory:', error)
      }
    }
  }

  // Fleet-wide view: plot every vehicle with a valid position whenever the
  // list changes. Re-frame the viewport only when the SET of vehicles changes
  // (first load, or the Fleet Vehicles list was filtered) — periodic position
  // refreshes must not yank the viewport away from the user.
  useEffect(() => {
    if (!mapInstanceRef.current) return
    const signature = vehicles.filter(v => vehicleHasValidPosition(v)).map(v => v.id).sort().join(',')
    plottedVehiclesSigRef.current = signature
    // Frame the fleet ONLY on the first plot. Later set changes (a device
    // gaining/losing GPS between polls, list filtering) must not move the
    // camera while the user is working.
    const shouldFit = signature !== '' && !hasFitBoundsRef.current
    updateVehicleMarkers(shouldFit)
    if (shouldFit) hasFitBoundsRef.current = true
  }, [vehicles, selectedVehicle, soloVehicle])

  useEffect(() => () => {
    markerAnimationsRef.current.forEach(frame => cancelAnimationFrame(frame))
    markerAnimationsRef.current.clear()
  }, [])

  // Follow toggle: sync the ref and bring the vehicle into view immediately
  // when enabled (zoom untouched).
  useEffect(() => {
    followVehicleRef.current = followVehicle
    if (followVehicle && selectedVehicle && vehicleHasValidPosition(selectedVehicle) && mapInstanceRef.current) {
      mapInstanceRef.current.panTo({
        lat: selectedVehicle.location.latitude,
        lng: selectedVehicle.location.longitude
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [followVehicle])

  // Load trajectory and chart data when selected vehicle changes
  useEffect(() => {
    // Create AbortController for this effect
    const abortController = new AbortController()
    
    if (selectedVehicle && mapInstanceRef.current) {
      console.log(`🚗 Selected vehicle changed to: ${selectedVehicle.name}`)

      // Always bring the selected vehicle into view immediately, even when it
      // has no trajectory data — otherwise clicking a vehicle does nothing
      // visible if the user previously panned or zoomed away.
      if (vehicleHasValidPosition(selectedVehicle)) {
        const pos = { lat: selectedVehicle.location.latitude, lng: selectedVehicle.location.longitude }
        mapInstanceRef.current.panTo(pos)
        const zoom = mapInstanceRef.current.getZoom()
        if (zoom != null && zoom < 10) mapInstanceRef.current.setZoom(13)
      }

      // Clear all existing trajectories first to prevent overlap
      clearAllTrajectories()
      
      // Load trajectory and chart data with abort signal
      const loadData = async () => {
        try {
          await Promise.all([
            loadVehicleTrajectory(selectedVehicle.id, abortController.signal),
            loadChartData(selectedVehicle.id, abortController.signal)
          ])
        } catch (error) {
          // Only log non-abort errors
          if (error.name !== 'AbortError') {
            console.error('❌ Error loading vehicle data:', error)
          }
        }
      }
      
      loadData()
    } else {
      // Clear all trajectories if no vehicle selected
      clearAllTrajectories()
      // Clear chart data
      setSpeedData([])
      setStopEvents([])
      setEventData([])
    }

    // Cleanup function to abort requests when effect re-runs or component unmounts
    return () => {
      abortController.abort()
    }
  }, [selectedVehicle?.id])

  // Reload trajectory when shared date range changes
  useEffect(() => {
    if (selectedVehicle && mapInstanceRef.current && sharedDateRange) {
      console.log(`📅 Shared date range changed, reloading trajectory for ${selectedVehicle.name}`)
      const abortController = new AbortController()
      
      // Clear all existing trajectories first
      clearAllTrajectories()
      
      const reloadData = async () => {
        try {
          await Promise.all([
            loadVehicleTrajectory(selectedVehicle.id, abortController.signal),
            loadChartData(selectedVehicle.id, abortController.signal)
          ])
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error('❌ Error reloading trajectory data:', error)
          }
        }
      }
      
      reloadData()
      
      return () => {
        abortController.abort()
      }
    }
  }, [sharedDateRange, selectedVehicle?.id])

  // Keep the displayed route growing while the selected vehicle is live. The
  // fleet poll updates the puck; this companion poll appends the newest
  // trajectory/chart samples without blanking the route during the request.
  useEffect(() => {
    if (!selectedVehicle?.id || !mapInstanceRef.current) return
    let refreshing = false
    let controller: AbortController | null = null
    const refresh = async () => {
      if (refreshing || document.visibilityState !== 'visible') return
      refreshing = true
      controller = new AbortController()
      try {
        await Promise.all([
          loadVehicleTrajectory(selectedVehicle.id, controller.signal, true),
          loadChartData(selectedVehicle.id, controller.signal, true)
        ])
      } catch (error: any) {
        if (error?.name !== 'AbortError') console.error('Live trajectory refresh failed:', error)
      } finally {
        refreshing = false
      }
    }
    const timer = window.setInterval(refresh, Math.max(10, markerRefreshSeconds) * 1000)
    return () => {
      window.clearInterval(timer)
      controller?.abort()
    }
    // Refresh cadence and date range define the live trajectory query.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVehicle?.id, dateRange.startDate, dateRange.endDate, markerRefreshSeconds])

  // Update legend visibility when showLegend state changes
  useEffect(() => {
    if (mapInstanceRef.current) {
      addTrajectoryLegend()
      // Update existing button instead of creating new one
      updateLegendToggleButton()
    }
  }, [showLegend])

  useEffect(() => {
    if (!mapInstanceRef.current) return
    trajectoryMarkersRef.current.forEach(marker => {
      const meta = (marker as any).__markerMeta
      if (meta?.type !== 'badge') return
      marker.setMap(markerShouldBeVisible(meta.kind as BadgeKind) ? mapInstanceRef.current : null)
    })
    eventMarkersRef.current.forEach(marker => {
      const meta = (marker as any).__markerMeta
      if (meta?.type !== 'badge') return
      marker.setMap(markerShouldBeVisible(meta.kind as BadgeKind) ? mapInstanceRef.current : null)
    })
    reclusterRouteBadges()
    addTrajectoryLegend()
  }, [visibleMarkerKinds])

  // Handle both internal and external chart hover (unified timeline hover)
  useEffect(() => {
    console.log(`🎯 Timeline hover effect triggered:`, { timelineHover, hoveredChartPoint, mapReady: !!mapInstanceRef.current })
    
    if (!mapInstanceRef.current) {
      console.warn(`⚠️ Map not ready for timeline hover`)
      return
    }

    // Use external hover if available, otherwise use internal hover
    const currentHover = timelineHover?.position ? timelineHover : hoveredChartPoint
    console.log(`🎯 Current hover:`, currentHover)

    if (currentHover?.position && currentHover.timestamp) {
      console.log(`✅ Creating/updating timeline marker at:`, currentHover.position)
      
      // Create or update timeline hover marker
      if (timelineMarkerRef.current) {
        console.log(`🔄 Updating existing timeline marker`)
        timelineMarkerRef.current.setPosition(currentHover.position)
        timelineMarkerRef.current.setTitle(`Timeline Position - ${currentHover.timestamp}`)
      } else {
        console.log(`🆕 Creating new timeline marker`)
        // Use vehicle icon for timeline marker
        const vehicleIcon = getCmaTruckIcon(selectedVehicle || undefined, 30)

        timelineMarkerRef.current = new google.maps.Marker({
          position: currentHover.position,
          map: mapInstanceRef.current,
          icon: {
            ...vehicleIcon,
            scaledSize: new google.maps.Size(30, 30),
            anchor: new google.maps.Point(15, 15)
          },
          title: `${selectedVehicle?.name || 'Vehicle'} - ${currentHover.timestamp}`,
          zIndex: 2000,
          animation: google.maps.Animation.DROP
        })

        // Add click handler for timeline marker
        timelineMarkerRef.current.addListener('click', () => {
          const infoWindow = new google.maps.InfoWindow({
            content: `
              <div class="p-3">
                <div class="font-semibold text-gray-900 mb-2">📍 Position Timeline</div>
                <div class="text-sm space-y-1">
                  <div><span class="font-medium">Heure:</span> ${currentHover.timestamp}</div>
                  <div><span class="font-medium">Coordonnées:</span> ${currentHover.position.lat.toFixed(6)}, ${currentHover.position.lng.toFixed(6)}</div>
                  <div class="text-xs text-gray-500 mt-2">Position du véhicule à ce moment précis</div>
                </div>
              </div>
            `
          })
          infoWindow.open(mapInstanceRef.current, timelineMarkerRef.current)
        })
      }

      // Center map on timeline position if it's far from current view
      const bounds = mapInstanceRef.current.getBounds()
      if (bounds && !bounds.contains(currentHover.position)) {
        mapInstanceRef.current.panTo(currentHover.position)
      }

    } else if (timelineMarkerRef.current) {
      // Remove timeline marker when not hovering
      timelineMarkerRef.current.setMap(null)
      timelineMarkerRef.current = null
    }
  }, [timelineHover, hoveredChartPoint])

  // Handle drawing mode changes
  useEffect(() => {
    if (isDrawingMode && mapInstanceRef.current) {
      enableDrawingMode()
    } else if (!isDrawingMode) {
      clearDrawing()
    }
  }, [isDrawingMode, drawingType])

  // Internal timeline hover handler for embedded charts
  const handleInternalTimelineHover = (timestamp: string | null, position: { lat: number, lng: number } | null) => {
    console.log(`🎯 Chart hover:`, { timestamp, position })
    setHoveredChartPoint({ timestamp, position })
    
    // Also notify external components if callback provided
    if (onTimelineHover) {
      onTimelineHover(timestamp, position)
    }
  }

  const selectReplayPoint = (index: number) => {
    if (speedData.length === 0) return
    const nextIndex = Math.max(0, Math.min(speedData.length - 1, index))
    setReplayIndex(nextIndex)
    const point = speedData[nextIndex]
    handleInternalTimelineHover(
      point.timestamp.toLocaleString(),
      { lat: point.latitude, lng: point.longitude }
    )
  }

  useEffect(() => {
    if (!replayPlaying || speedData.length === 0) return
    const delay = Math.max(60, Math.min(600, 15_000 / speedData.length)) / replayRate
    const timer = window.setInterval(() => {
      setReplayIndex(current => {
        if (current >= speedData.length - 1) {
          setReplayPlaying(false)
          return current
        }
        const next = current + 1
        const point = speedData[next]
        handleInternalTimelineHover(
          point.timestamp.toLocaleString(),
          { lat: point.latitude, lng: point.longitude }
        )
        return next
      })
    }, delay)
    return () => window.clearInterval(timer)
    // The replay intentionally uses the current chart dataset as one sequence.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replayPlaying, replayRate, speedData])

  useEffect(() => {
    setReplayPlaying(false)
    setReplayIndex(0)
  }, [selectedVehicle?.id, dateRange.startDate, dateRange.endDate])

  const renderReplayControls = () => {
    const point = speedData[replayIndex]
    return (
      <div className="mx-3 mt-3 flex items-center gap-3 rounded-2xl border border-slate-200 bg-white/95 px-3 py-2 shadow-sm">
        <div className="flex items-center gap-1">
          <button
            onClick={() => selectReplayPoint(0)}
            disabled={!speedData.length}
            title={t('restart_replay', { defaultValue: 'Restart replay' })}
            className="rounded-xl p-2 text-slate-600 hover:bg-slate-100 disabled:opacity-30"
          >
            <RotateCcw size={16} />
          </button>
          <button
            onClick={() => selectReplayPoint(replayIndex - 1)}
            disabled={!speedData.length || replayIndex <= 0}
            title={t('previous_point', { defaultValue: 'Previous point' })}
            className="rounded-xl p-2 text-slate-600 hover:bg-slate-100 disabled:opacity-30"
          >
            <SkipBack size={16} />
          </button>
          <button
            onClick={() => {
              if (!replayPlaying && replayIndex >= speedData.length - 1) selectReplayPoint(0)
              setReplayPlaying(value => !value)
            }}
            disabled={!speedData.length}
            title={replayPlaying ? t('pause_replay', { defaultValue: 'Pause replay' }) : t('play_replay', { defaultValue: 'Play replay' })}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-600 text-white shadow hover:bg-blue-700 disabled:bg-slate-300"
          >
            {replayPlaying ? <Pause size={17} /> : <Play size={17} className="ml-0.5" />}
          </button>
          <button
            onClick={() => selectReplayPoint(replayIndex + 1)}
            disabled={!speedData.length || replayIndex >= speedData.length - 1}
            title={t('next_point', { defaultValue: 'Next point' })}
            className="rounded-xl p-2 text-slate-600 hover:bg-slate-100 disabled:opacity-30"
          >
            <SkipForward size={16} />
          </button>
        </div>
        <input
          type="range"
          min={0}
          max={Math.max(0, speedData.length - 1)}
          value={Math.min(replayIndex, Math.max(0, speedData.length - 1))}
          onChange={event => {
            setReplayPlaying(false)
            selectReplayPoint(Number(event.target.value))
          }}
          disabled={!speedData.length}
          aria-label={t('trajectory_timeline', { defaultValue: 'Trajectory timeline' })}
          className="min-w-24 flex-1 accent-blue-600"
        />
        <div className="min-w-[120px] text-right">
          <div className="text-xs font-semibold text-slate-700">
            {point ? point.timestamp.toLocaleString([], { dateStyle: 'short', timeStyle: 'medium' }) : '—'}
          </div>
          <div className="text-[10px] text-slate-400">
            {speedData.length ? `${replayIndex + 1} / ${speedData.length} · ${point?.speed.toFixed(1)} km/h` : t('no_data', { defaultValue: 'No data' })}
          </div>
        </div>
        <select
          value={replayRate}
          onChange={event => setReplayRate(Number(event.target.value))}
          title={t('replay_speed', { defaultValue: 'Replay speed' })}
          className="rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-semibold text-slate-600"
        >
          <option value={0.5}>0.5×</option>
          <option value={1}>1×</option>
          <option value={2}>2×</option>
          <option value={4}>4×</option>
        </select>
      </div>
    )
  }

  // Load chart data for tabs with date range filtering
  const loadChartData = async (
    vehicleId: string,
    abortSignal?: AbortSignal,
    preserveReplay = false
  ) => {
    if (!vehicleId) return
    
    setChartLoading(true)
    try {
      console.log(`📊 Loading chart data for vehicle: ${vehicleId} from ${dateRange.startDate} to ${dateRange.endDate}`)
      
      // Calculate hours from date range (no artificial limit)
      const startTime = new Date(dateRange.startDate).getTime()
      const endTime = new Date(dateRange.endDate).getTime()
      const hours = Math.ceil((endTime - startTime) / (1000 * 60 * 60))
      
      console.log(`⏰ Requesting ${hours} hours of chart data for full date range`)
      
      const response = await apiClient.get(`/timeline/${vehicleId}`, { 
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        hours: hours
      }, {
        signal: abortSignal // Pass signal in axios config, not as query param
      })
      
      if (response.data && Array.isArray(response.data)) {
        console.log(`📊 Raw timeline data received: ${response.data.length} points`)
        
        // Process speed data with better location handling
        const speeds = response.data
          .filter((point: any) => point.timestamp && point.location)
          .map((point: any) => ({
            time: new Date(point.timestamp).toLocaleTimeString('fr-FR', { 
              hour: '2-digit', 
              minute: '2-digit' 
            }),
            speed: parseFloat(point.speed || 0),
            timestamp: new Date(point.timestamp),
            latitude: parseFloat(point.location?.latitude || 0),
            longitude: parseFloat(point.location?.longitude || 0)
          }))
          .filter((point: any) => point.latitude !== 0 && point.longitude !== 0)
          .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())
          .map((point, index) => ({ ...point, index }))

        console.log(`📈 Processed speed data: ${speeds.length} valid points`)
        console.log(`📍 Sample speed point:`, speeds[0])
        setSpeedData(speeds)
        if (!preserveReplay) {
          setReplayIndex(0)
          setReplayPlaying(false)
        } else {
          setReplayIndex(current => Math.min(current, Math.max(0, speeds.length - 1)))
        }

        // Process stop events
        const stops = detectStopEventsFromData(response.data)
        console.log(`🛑 Processed stop events: ${stops.length} stops`)
        console.log(`📍 Sample stop event:`, stops[0])
        setStopEvents(stops)
        setEventData(response.data)
        
        console.log(`✅ Chart data loaded successfully`)
      } else {
        console.warn('⚠️ No timeline data received')
      }
    } catch (error) {
      // Only log non-abort errors
      if (error.name !== 'AbortError') {
        console.error('❌ Error loading chart data:', error)
      }
    } finally {
      setChartLoading(false)
    }
  }

  // Handle tab actions
  const handleTabAction = (tab: 'map' | 'speed' | 'stops' | 'timeline' | 'stats') => {
    setActiveTab(tab)
    setShowTabContent(true)
    
    // Every analytics tab shares the same synchronized replay dataset.
    if (tab !== 'map' && selectedVehicle && speedData.length === 0) {
      loadChartData(selectedVehicle.id)
    }
    
    switch (tab) {
      case 'map':
        setReplayPlaying(false)
        if (selectedVehicle && mapInstanceRef.current) {
          mapInstanceRef.current.setCenter({
            lat: selectedVehicle.location.latitude,
            lng: selectedVehicle.location.longitude
          })
          mapInstanceRef.current.setZoom(15)
        }
        break
        
      case 'speed':
        console.log('📈 Speed analysis activated')
        break
        
      case 'stops':
        console.log('🛑 Stop analysis activated')
        break
        
      case 'timeline':
        console.log('⏰ Timeline analysis activated')
        break
        
      case 'stats':
        console.log('📊 Statistics view activated')
        break
    }
  }

  if (error) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-gray-100">
        <div className="text-center p-8">
          <div className="text-red-500 text-4xl mb-4">❌</div>
          <div className="text-lg font-semibold text-gray-800 mb-2">Map Error</div>
          <div className="text-gray-600">{error}</div>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Reload Page
          </button>
        </div>
      </div>
    )
  }

  // Handle tab content rendering
  const renderTabContent = () => {
    if (chartLoading) {
      return (
        <div className="p-8 bg-white flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
          <span className="text-gray-600">Chargement des données...</span>
        </div>
      )
    }

    switch (activeTab) {
      case 'speed':
        return (
          <div className="p-3 bg-white">
            <div className="flex items-center justify-between mb-2">
              <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                <BarChart3 size={16} className="text-blue-600" />
                {t('speed_analysis', { defaultValue: 'Speed analysis' })}
              </h4>
              <div className="flex items-center space-x-4 text-sm">
                <div className="bg-red-50 px-3 py-1 rounded">
                  <span className="text-red-600 font-bold">{trajectoryStats.maxSpeed} km/h</span>
                  <span className="text-red-800 text-xs ml-1">Max</span>
                </div>
                <div className="bg-blue-50 px-3 py-1 rounded">
                  <span className="text-blue-600 font-bold">{trajectoryStats.avgSpeed.toFixed(1)} km/h</span>
                  <span className="text-blue-800 text-xs ml-1">Moy</span>
                </div>
              </div>
            </div>
            
            {speedData.length > 0 ? (
              <div className="h-32">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart 
                    data={speedData}
                    onMouseMove={(data: any) => {
                      console.log(`🖱️ Speed chart mouse move:`, data)
                      if (data && data.activePayload && data.activePayload[0]) {
                        const point = data.activePayload[0].payload
                        setReplayPlaying(false)
                        setReplayIndex(point.index)
                        console.log(`📍 Speed chart point:`, point)
                        if (point.latitude && point.longitude && point.latitude !== 0 && point.longitude !== 0) {
                          console.log(`✅ Calling handleInternalTimelineHover with:`, point.time, { lat: point.latitude, lng: point.longitude })
                          handleInternalTimelineHover(point.time, { lat: point.latitude, lng: point.longitude })
                        } else {
                          console.warn(`⚠️ Invalid coordinates:`, { lat: point.latitude, lng: point.longitude })
                        }
                      }
                    }}
                    onMouseLeave={() => handleInternalTimelineHover(null, null)}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis 
                      dataKey="time"
                      tick={{ fontSize: 10 }}
                      interval="preserveStartEnd"
                    />
                    <YAxis 
                      tick={{ fontSize: 10 }}
                      label={{ value: 'km/h', angle: -90, position: 'insideLeft' }}
                    />
                    <Tooltip 
                      formatter={(value: number, name: string, props: any) => {
                        const point = props.payload
                        const locationText = point.latitude && point.longitude ? 
                          ` | 📍 ${point.latitude.toFixed(4)}, ${point.longitude.toFixed(4)}` : ''
                        return [`${value.toFixed(1)} km/h${locationText}`, 'Vitesse']
                      }}
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #ccc',
                        borderRadius: '8px',
                        fontSize: '12px'
                      }}
                    />
                    <defs>
                      <linearGradient id="speedGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.1}/>
                      </linearGradient>
                    </defs>
                    <Area
                      type="monotone"
                      dataKey="speed"
                      stroke="#3b82f6"
                      strokeWidth={2}
                      fill="url(#speedGradient)"
                    />
                    {speedData[replayIndex] && (
                      <ReferenceLine x={speedData[replayIndex].time} stroke="#2563eb" strokeDasharray="3 3" />
                    )}
                    {/* Speed limit lines */}
                    <Line 
                      type="monotone" 
                      dataKey={() => 50} 
                      stroke="#f59e0b" 
                      strokeDasharray="5 5" 
                      strokeWidth={1}
                      dot={false}
                    />
                    <Line 
                      type="monotone" 
                      dataKey={() => 90} 
                      stroke="#ef4444" 
                      strokeDasharray="5 5" 
                      strokeWidth={1}
                      dot={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-20 flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <BarChart3 size={24} className="mx-auto mb-2 text-gray-400" />
                  <div className="text-sm">Aucune donnée de vitesse</div>
                </div>
              </div>
            )}
          </div>
        )
      
      case 'stops':
        const stopChartData = stopEvents.map((stop, index) => ({
          name: `Arrêt ${index + 1}`,
          duration: stop.durationMinutes || 0,
          startTime: stop.startTime?.toLocaleTimeString() || '',
          location: `${stop.latitude?.toFixed(4) || 0}, ${stop.longitude?.toFixed(4) || 0}`,
          coordinates: { lat: stop.latitude || 0, lng: stop.longitude || 0 }
        }))

        const eventTypeData = stopEvents.reduce((acc: any[], stop) => {
          const type = (stop.durationHours || 0) >= 5 ? 'Parking Long' : 'Arrêt Court'
          const existing = acc.find(item => item.name === type)
          if (existing) {
            existing.value++
          } else {
            acc.push({ name: type, value: 1 })
          }
          return acc
        }, [])

        const COLORS = ['#F59E0B', '#8B5CF6', '#10B981', '#EF4444']

        return (
          <div className="p-3 bg-white">
            <div className="flex items-center justify-between mb-2">
              <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                <Pause size={16} className="text-amber-600" />
                {t('stop_analysis', { defaultValue: 'Stop analysis' })}
              </h4>
              <div className="flex items-center space-x-3 text-sm">
                <div className="bg-orange-50 px-3 py-1 rounded">
                  <span className="text-orange-600 font-bold">{stopEvents.length}</span>
                  <span className="text-orange-800 text-xs ml-1">Total</span>
                </div>
                <div className="bg-yellow-50 px-3 py-1 rounded">
                  <span className="text-yellow-600 font-bold">{stopEvents.filter(s => (s.durationHours || 0) < 5).length}</span>
                  <span className="text-yellow-800 text-xs ml-1">Courts</span>
                </div>
                <div className="bg-purple-50 px-3 py-1 rounded">
                  <span className="text-purple-600 font-bold">{stopEvents.filter(s => (s.durationHours || 0) >= 5).length}</span>
                  <span className="text-purple-800 text-xs ml-1">Longs</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              {/* Stop duration bar chart */}
              <div>
                <h5 className="text-xs font-medium text-gray-700 mb-1">Durée des Arrêts</h5>
                {stopChartData.length > 0 ? (
                  <div className="h-32">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart 
                        data={stopChartData}
                        onMouseMove={(data: any) => {
                          console.log(`🖱️ Stop chart mouse move:`, data)
                          if (data && data.activePayload && data.activePayload[0]) {
                            const stopData = data.activePayload[0].payload
                            console.log(`📍 Stop chart data:`, stopData)
                            if (stopData.coordinates && stopData.coordinates.lat !== 0 && stopData.coordinates.lng !== 0) {
                              console.log(`✅ Calling handleInternalTimelineHover with stop:`, stopData.startTime, stopData.coordinates)
                              handleInternalTimelineHover(stopData.startTime, stopData.coordinates)
                            } else {
                              console.warn(`⚠️ Invalid stop coordinates:`, stopData.coordinates)
                            }
                          }
                        }}
                        onMouseLeave={() => handleInternalTimelineHover(null, null)}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 10 }} label={{ value: 'min', angle: -90, position: 'insideLeft' }} />
                        <Tooltip 
                          formatter={(value: any, name: string, props: any) => {
                            const stopData = props.payload
                            return [`${value} minutes | 📍 ${stopData.location}`, 'Durée']
                          }}
                        />
                        <Bar dataKey="duration" fill="#ef4444" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-24 flex items-center justify-center text-gray-500">
                    <div className="text-center">
                      <Activity size={16} className="mx-auto mb-1 text-gray-400" />
                      <div className="text-xs">Aucun arrêt détecté</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Event type pie chart */}
              <div>
                <h5 className="text-xs font-medium text-gray-700 mb-1">Types d'Arrêts</h5>
                {eventTypeData.length > 0 ? (
                  <div className="h-32">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={eventTypeData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          outerRadius={40}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {eventTypeData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-24 flex items-center justify-center text-gray-500">
                    <div className="text-center">
                      <Activity size={16} className="mx-auto mb-1 text-gray-400" />
                      <div className="text-xs">Aucune donnée d'arrêt</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )
      
      case 'timeline':
        return (
          <div className="p-3 bg-white">
            <div className="flex items-center justify-between mb-2">
              <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                <Clock size={16} className="text-blue-600" />
                {t('interactive_timeline', { defaultValue: 'Interactive timeline' })}
              </h4>
              <div className="text-xs text-gray-500">
                {speedData.length} points GPS
              </div>
            </div>
            
            {speedData.length > 0 ? (
              <div className="space-y-3">
                {/* Timeline scrubber */}
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-gray-700">Navigation Timeline</span>
                    <span className="text-xs text-gray-500">
                      {trajectoryStats.startTime?.toLocaleTimeString()} → {trajectoryStats.endTime?.toLocaleTimeString()}
                    </span>
                  </div>
                  
                  {/* Simple timeline visualization */}
                  <div className="h-24">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart 
                        data={speedData}
                        onMouseMove={(data: any) => {
                          if (data && data.activePayload && data.activePayload[0]) {
                            const point = data.activePayload[0].payload
                            setReplayPlaying(false)
                            setReplayIndex(point.index)
                            if (point.latitude && point.longitude && point.latitude !== 0 && point.longitude !== 0) {
                              handleInternalTimelineHover(point.time, { lat: point.latitude, lng: point.longitude })
                            }
                          }
                        }}
                        onMouseLeave={() => handleInternalTimelineHover(null, null)}
                      >
                        <XAxis dataKey="time" hide />
                        <YAxis hide />
                        <Tooltip 
                          formatter={(value: number, name: string, props: any) => {
                            const point = props.payload
                            return [`📍 ${point.latitude?.toFixed(4)}, ${point.longitude?.toFixed(4)}`, 'Position']
                          }}
                          labelFormatter={(label) => `Heure: ${label}`}
                        />
                        <Line 
                          type="monotone" 
                          dataKey="speed" 
                          stroke="#3b82f6" 
                          strokeWidth={2}
                          dot={false}
                        />
                        {speedData[replayIndex] && (
                          <ReferenceLine x={speedData[replayIndex].time} stroke="#2563eb" strokeDasharray="3 3" />
                        )}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                {/* Current position info */}
                {hoveredChartPoint.position && (
                  <div className="bg-blue-50 p-3 rounded-lg">
                    <div className="flex items-center space-x-2 mb-2">
                      <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                      <span className="text-sm font-medium text-blue-800">Position Actuelle</span>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-xs">
                      <div>
                        <span className="text-gray-600">Heure:</span>
                        <div className="font-medium text-gray-800">{hoveredChartPoint.timestamp}</div>
                      </div>
                      <div>
                        <span className="text-gray-600">Coordonnées:</span>
                        <div className="font-medium text-gray-800">
                          {hoveredChartPoint.position.lat.toFixed(4)}, {hoveredChartPoint.position.lng.toFixed(4)}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Instructions */}
                <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                  💡 Survolez la timeline ci-dessus ou les onglets Vitesse/Arrêts pour voir la position du véhicule sur la carte.
                </div>
              </div>
            ) : (
              <div className="h-24 flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <Clock size={16} className="mx-auto mb-1 text-gray-400" />
                  <div className="text-xs">Aucune donnée timeline</div>
                </div>
              </div>
            )}
          </div>
        )
      
      case 'stats':
        const stoppedMinutes = stopEvents.reduce((total, stop) =>
          total + Number(stop.durationMinutes || (stop.durationHours || 0) * 60), 0)
        const movingMinutes = Math.max(0, trajectoryStats.totalDuration - stoppedMinutes)
        const movingPercent = trajectoryStats.totalDuration > 0
          ? (movingMinutes / trajectoryStats.totalDuration) * 100
          : 0
        const orderedSpeeds = speedData.map(point => point.speed).sort((a, b) => a - b)
        const percentile95 = orderedSpeeds.length
          ? orderedSpeeds[Math.min(orderedSpeeds.length - 1, Math.floor(orderedSpeeds.length * 0.95))]
          : 0
        const durationLabel = (minutes: number) =>
          `${Math.floor(minutes / 60)}h ${Math.round(minutes % 60)}m`
        const statCards = [
          { label: t('distance', { defaultValue: 'Distance' }), value: `${trajectoryStats.totalDistance.toFixed(2)} km`, icon: <Route size={17} />, tone: 'text-blue-600 bg-blue-50' },
          { label: t('trip_duration', { defaultValue: 'Trip duration' }), value: durationLabel(trajectoryStats.totalDuration), icon: <Timer size={17} />, tone: 'text-violet-600 bg-violet-50' },
          { label: t('average_speed', { defaultValue: 'Average speed' }), value: `${trajectoryStats.avgSpeed.toFixed(1)} km/h`, icon: <Gauge size={17} />, tone: 'text-emerald-600 bg-emerald-50' },
          { label: t('maximum_speed', { defaultValue: 'Maximum speed' }), value: `${trajectoryStats.maxSpeed.toFixed(1)} km/h`, icon: <TrendingUp size={17} />, tone: 'text-red-600 bg-red-50' },
          { label: t('speed_p95', { defaultValue: '95th percentile speed' }), value: `${percentile95.toFixed(1)} km/h`, icon: <BarChart3 size={17} />, tone: 'text-cyan-600 bg-cyan-50' },
          { label: t('moving_time', { defaultValue: 'Moving time' }), value: `${durationLabel(movingMinutes)} · ${movingPercent.toFixed(0)}%`, icon: <Play size={17} />, tone: 'text-teal-600 bg-teal-50' },
          { label: t('stopped_time', { defaultValue: 'Stopped time' }), value: durationLabel(stoppedMinutes), icon: <Pause size={17} />, tone: 'text-amber-600 bg-amber-50' },
          { label: t('detected_stops', { defaultValue: 'Detected stops' }), value: `${stopEvents.length} · ${stopEvents.filter(stop => (stop.durationHours || 0) >= 5).length} parking`, icon: <MapPin size={17} />, tone: 'text-orange-600 bg-orange-50' },
          { label: t('gps_points', { defaultValue: 'GPS points' }), value: speedData.length.toLocaleString(), icon: <Database size={17} />, tone: 'text-slate-600 bg-slate-100' }
        ]
        return (
          <div className="bg-gradient-to-b from-white to-slate-50 p-4">
            <div className="mb-3 flex items-center justify-between">
              <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                <Gauge size={17} className="text-blue-600" />
                {t('detailed_statistics', { defaultValue: 'Detailed statistics' })}
              </h4>
              <div className="text-right text-[11px] text-slate-500">
                <div>{trajectoryStats.startTime?.toLocaleString() || '—'}</div>
                <div>{trajectoryStats.endTime?.toLocaleString() || '—'}</div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
              {statCards.map(card => (
                <div key={card.label} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
                  <div className={`rounded-xl p-2 ${card.tone}`}>{card.icon}</div>
                  <div className="min-w-0">
                    <div className="truncate text-[11px] font-medium text-slate-500">{card.label}</div>
                    <div className="truncate text-sm font-bold text-slate-800">{card.value}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      
      default:
        return null
    }
  }

  return (
    <div className="w-full h-full relative flex flex-col">
      {/* Map container */}
      <div 
        ref={mapRefCallback}
        className="flex-1 w-full"
        style={{ minHeight: '300px' }}
      />
      
      {/* Loading overlay */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100/90 backdrop-blur-sm">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <div className="text-lg font-semibold text-gray-800">Loading Google Maps...</div>
            <div className="text-sm text-gray-600 mt-2">Please wait while we initialize the map</div>
          </div>
        </div>
      )}

      {/* Live tracking warnings (closable) */}
      <MapWarningsPanel
        vehicles={vehicles}
        closed={warningsClosed}
        onClose={() => {
          sessionStorage.setItem('fleet_map_warnings_dismissed', '1')
          setWarningsClosed(true)
        }}
        onCount={setWarningCount}
        onVehicleSelect={onVehicleSelect}
        onOpenDetail={onOpenAlertDetail}
      />

      {/* Map View Controls - Floating */}
      <div className="absolute top-4 right-4 flex flex-col space-y-2 z-40">
        {/* Satellite View Toggle */}
        <button
          onClick={toggleSatelliteView}
          className={`p-3 rounded-lg shadow-lg transition-colors ${
            isSatelliteView 
              ? 'bg-blue-600 text-white' 
              : 'bg-white text-gray-700 hover:bg-gray-50'
          }`}
          title={isSatelliteView ? 'Switch to Map View' : 'Switch to Satellite View'}
        >
          {isSatelliteView ? (
            <MapIcon className="w-5 h-5" />
          ) : (
            <Satellite className="w-5 h-5" />
          )}
        </button>

        {/* Follow selected vehicle (pan with it, zoom preserved) */}
        {selectedVehicle && (
          <button
            onClick={() => setFollowVehicle(v => !v)}
            className={`p-3 rounded-lg shadow-lg transition-colors ${
              followVehicle
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700'
            }`}
            title={followVehicle ? t('map_controls.follow_off', 'Stop following the vehicle') : t('map_controls.follow_on', 'Follow the vehicle as it moves')}
          >
            <Navigation className="w-5 h-5" />
          </button>
        )}

        {/* Only the selected vehicle on the map; a second click brings the fleet back */}
        {selectedVehicle && (
          <button
            onClick={() => setSoloVehicle(v => !v)}
            className={`p-3 rounded-lg shadow-lg transition-colors ${
              soloVehicle
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700'
            }`}
            title={soloVehicle ? t('map_controls.solo_off', 'Show the whole fleet again') : t('map_controls.solo_on', 'Show this vehicle only')}
          >
            <Focus className="w-5 h-5" />
          </button>
        )}

        {warningsClosed && (
          <button
            type="button"
            onClick={() => {
              sessionStorage.removeItem('fleet_map_warnings_dismissed')
              setWarningsClosed(false)
            }}
            className="relative p-3 rounded-lg bg-white text-gray-700 shadow-lg hover:bg-gray-50 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            title={t('warnings_show', { defaultValue: 'Show warnings' })}
          >
            <AlertTriangle className="w-5 h-5 text-red-500" />
            {warningCount > 0 && (
              <span className="absolute -right-1 -top-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                {warningCount}
              </span>
            )}
          </button>
        )}
      </div>

      {/* Drawing Controls */}
      {isDrawingMode && (
        <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-md rounded-lg shadow-lg border border-gray-200 p-4 z-40">
          <div className="text-sm font-medium text-gray-800 mb-3">
            🎨 Drawing Mode: {drawingType}
          </div>
          <div className="space-y-2 text-xs text-gray-600 mb-4">
            <div>• Click on map to add points</div>
            <div>• Click markers to remove points</div>
            <div>• Need at least 2 points</div>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={completeDrawing}
              disabled={drawingPoints.length < 2}
              className="px-3 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              ✅ Complete ({drawingPoints.length})
            </button>
            <button
              onClick={clearDrawing}
              className="px-3 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700"
            >
              ❌ Clear
            </button>
          </div>
        </div>
      )}

      {/* Footer toolbar with tabs (like in your image) */}
      <div className="bg-white border-t border-gray-200">
        {/* Tab navigation bar */}
        <div className="flex items-center justify-between gap-2 px-2 py-1 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center space-x-0.5 shrink-0">
            <button
              onClick={() => handleTabAction('map')}
              className={`px-2 py-0.5 rounded text-xs font-medium transition-colors flex items-center space-x-1 ${
                activeTab === 'map' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
              }`}
            >
              <MapPin size={14} />
              <span>{t('map', { defaultValue: 'Map' })}</span>
            </button>
            <button
              onClick={() => handleTabAction('speed')}
              className={`px-2 py-0.5 rounded text-xs font-medium transition-colors flex items-center space-x-1 ${
                activeTab === 'speed' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
              }`}
            >
              <BarChart3 size={14} />
              <span>{t('speed', { defaultValue: 'Speed' })}</span>
            </button>
            <button
              onClick={() => handleTabAction('stops')}
              className={`px-2 py-0.5 rounded text-xs font-medium transition-colors flex items-center space-x-1 ${
                activeTab === 'stops' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
              }`}
            >
              <Pause size={14} />
              <span>{t('stops', { defaultValue: 'Stops' })}</span>
            </button>
            <button
              onClick={() => handleTabAction('timeline')}
              className={`px-2 py-0.5 rounded text-xs font-medium transition-colors flex items-center space-x-1 ${
                activeTab === 'timeline' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
              }`}
            >
              <Clock size={14} />
              <span>{t('timeline', { defaultValue: 'Timeline' })}</span>
            </button>
            <button
              onClick={() => handleTabAction('stats')}
              className={`px-2 py-0.5 rounded text-xs font-medium transition-colors flex items-center space-x-1 ${
                activeTab === 'stats' ? 'bg-blue-100 text-blue-700' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'
              }`}
            >
              <Gauge size={14} />
              <span>{t('stats', { defaultValue: 'Stats' })}</span>
            </button>
          </div>

          {/* Right side - Vehicle info and controls */}
          <div className="flex items-center gap-2 min-w-0">
            {/* Map Controls */}
            <div className="flex items-center space-x-2">
              {/* Satellite View Toggle */}
              <button
                onClick={toggleSatelliteView}
                className={`p-1.5 rounded-lg transition-colors shrink-0 ${
                  isSatelliteView 
                    ? 'bg-blue-100 text-blue-700' 
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
                title={isSatelliteView ? 'Switch to Map View' : 'Switch to Satellite View'}
              >
                {isSatelliteView ? (
                  <MapIcon className="w-4 h-4" />
                ) : (
                  <Satellite className="w-4 h-4" />
                )}
              </button>

              {/* Date Range Filter for Map Data */}
              {selectedVehicle && (
                <>
                  <Filter className="w-4 h-4 text-gray-500" />
                  <CalendarButton
                    value={dateRange}
                    onChange={handleDateRangeChange}
                    size="sm"
                    className="!px-2 !py-1"
                    title="Trajectory Data Period"
                  />
                </>
              )}
            </div>
            
            {selectedVehicle && (
              <div className="text-xs font-medium text-gray-800 bg-blue-50 px-2 py-0.5 rounded truncate max-w-[9rem]" title={selectedVehicle.name}>
                {selectedVehicle.name}
              </div>
            )}
            <div className="text-xs text-gray-500 whitespace-nowrap hidden xl:block">
              {vehicles.length} véhicules
            </div>
            <button
              onClick={() => setShowTabContent(!showTabContent)}
              className="p-1 hover:bg-gray-200 rounded transition-colors"
              title={showTabContent ? 'Masquer' : 'Afficher'}
            >
              <ChevronDown size={16} className={`text-gray-500 transition-transform ${showTabContent ? 'rotate-180' : ''}`} />
            </button>
          </div>
        </div>

        {/* Tab content area */}
        {showTabContent && (
          <div className="border-t border-gray-200 bg-slate-50">
            {activeTab !== 'map' && renderReplayControls()}
            {renderTabContent()}
          </div>
        )}
      </div>
    </div>
  )
}

// Two Chaikin passes round sharp GPS corners while staying inside the recorded
// segments (unlike an overshooting spline). Large/time-separated jumps remain
// disconnected and every group keeps its real first/last GPS positions.
function smoothTrajectory(points: any[]): any[] {
  if (points.length < 3) return points

  const groups: any[][] = []
  let group: any[] = []
  for (const point of points) {
    const previous = group[group.length - 1]
    if (!previous) {
      group.push(point)
      continue
    }
    const distanceKm = calculateDistance(previous.lat, previous.lng, point.lat, point.lng)
    const timeGapMs = Math.abs(new Date(point.timestamp).getTime() - new Date(previous.timestamp).getTime())
    if (distanceKm > 5 || timeGapMs > 2 * 60 * 60_000) {
      groups.push(group)
      group = [{ ...point, breakBefore: true }]
    } else if (distanceKm >= 0.005) {
      group.push(point)
    }
  }
  if (group.length) groups.push(group)

  const mix = (a: any, b: any, ratio: number) => ({
    ...a,
    lat: a.lat + (b.lat - a.lat) * ratio,
    lng: a.lng + (b.lng - a.lng) * ratio,
    speed: Number(a.speed || 0) + (Number(b.speed || 0) - Number(a.speed || 0)) * ratio,
    timestamp: new Date(
      new Date(a.timestamp).getTime() +
      (new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()) * ratio
    ),
    event: null,
    breakBefore: false
  })
  const roundCorners = (input: any[]) => {
    if (input.length < 3) return input
    const output = [input[0]]
    for (let i = 0; i < input.length - 1; i++) {
      output.push(mix(input[i], input[i + 1], 0.25))
      output.push(mix(input[i], input[i + 1], 0.75))
    }
    output.push(input[input.length - 1])
    return output
  }

  return groups.flatMap((pointsInGroup, index) => {
    const rounded = roundCorners(roundCorners(pointsInGroup))
    if (index > 0 && rounded[0]) rounded[0] = { ...rounded[0], breakBefore: true }
    return rounded
  })
}

// Optimize trajectory points for performance
function optimizeTrajectoryPoints(points: any[], maxPoints: number): any[] {
  if (points.length <= maxPoints) return points

  const step = Math.ceil(points.length / maxPoints)
  const optimized: any[] = []

  for (let i = 0; i < points.length; i++) {
    if (
      i === 0 ||
      i === points.length - 1 ||
      points[i].breakBefore ||
      points[i + 1]?.breakBefore ||
      i % step === 0
    ) {
      optimized.push(points[i])
    }
  }

  // Always include the last point
  if (optimized[optimized.length - 1] !== points[points.length - 1]) {
    optimized.push(points[points.length - 1])
  }

  return optimized
}

// Detect stop events from timeline data
function detectStopEventsFromData(data: any[]): any[] {
  if (data.length < 2) return []

  const stopEvents: any[] = []
  let stopStart: any = null
  const STOP_SPEED_THRESHOLD = 1 // km/h
  const MIN_STOP_DURATION = 10 // minutes

  const sortedData = data
    .map((point: any) => ({
      ...point,
      timestamp: new Date(point.timestamp),
      speed: parseFloat(point.speed || 0),
      latitude: parseFloat(point.location?.latitude || 0),
      longitude: parseFloat(point.location?.longitude || 0)
    }))
    .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime())

  for (let i = 0; i < sortedData.length; i++) {
    const point = sortedData[i]
    const isStationary = point.speed < STOP_SPEED_THRESHOLD

    if (isStationary && !stopStart) {
      stopStart = point
    } else if (!isStationary && stopStart) {
      const durationMs = point.timestamp.getTime() - stopStart.timestamp.getTime()
      const durationMinutes = durationMs / (1000 * 60)
      const durationHours = durationMinutes / 60

      if (durationMinutes >= MIN_STOP_DURATION) {
        stopEvents.push({
          startTime: stopStart.timestamp,
          endTime: point.timestamp,
          latitude: stopStart.latitude,
          longitude: stopStart.longitude,
          durationMinutes: Math.round(durationMinutes),
          durationHours: durationHours,
          batteryLevel: stopStart.batteryLevel,
          signalStrength: stopStart.signalStrength
        })
      }
      stopStart = null
    }
  }

  return stopEvents
}

// Detect stop events in trajectory data
function detectStopEvents(points: any[]): any[] {
  if (points.length < 2) return []

  const stopEvents: any[] = []
  let stopStart: any = null
  const STOP_SPEED_THRESHOLD = 1 // km/h - consider stopped if speed < 1 km/h
  const MIN_STOP_DURATION = 10 // minutes - minimum duration to consider as a stop

  for (let i = 0; i < points.length; i++) {
    const point = points[i]
    const isStationary = point.speed < STOP_SPEED_THRESHOLD

    if (isStationary && !stopStart) {
      // Start of potential stop
      stopStart = point
    } else if (!isStationary && stopStart) {
      // End of stop - calculate duration
      const durationMs = point.timestamp.getTime() - stopStart.timestamp.getTime()
      const durationMinutes = durationMs / (1000 * 60)
      const durationHours = durationMinutes / 60

      if (durationMinutes >= MIN_STOP_DURATION) {
        stopEvents.push({
          startTime: stopStart.timestamp,
          endTime: point.timestamp,
          latitude: stopStart.lat,
          longitude: stopStart.lng,
          durationMinutes: Math.round(durationMinutes),
          durationHours: durationHours,
          batteryLevel: stopStart.batteryLevel,
          signalStrength: stopStart.signalStrength
        })
      }
      stopStart = null
    }
  }

  // Handle case where trajectory ends during a stop
  if (stopStart) {
    const lastPoint = points[points.length - 1]
    const durationMs = lastPoint.timestamp.getTime() - stopStart.timestamp.getTime()
    const durationMinutes = durationMs / (1000 * 60)
    const durationHours = durationMinutes / 60

    if (durationMinutes >= MIN_STOP_DURATION) {
      stopEvents.push({
        startTime: stopStart.timestamp,
        endTime: lastPoint.timestamp,
        latitude: stopStart.lat,
        longitude: stopStart.lng,
        durationMinutes: Math.round(durationMinutes),
        durationHours: durationHours,
        batteryLevel: stopStart.batteryLevel,
        signalStrength: stopStart.signalStrength
      })
    }
  }

  return stopEvents
}

function formatStopInstant(value: Date): string {
  return value.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
  })
}

function cardNode(tag: string, className: string, text?: string): HTMLElement {
  const node = document.createElement(tag)
  node.className = className
  if (text != null) node.textContent = text
  return node
}

type MapCardTone = 'amber' | 'purple' | 'green' | 'red' | 'blue'
type MapCardGlyph = 'pause' | 'p' | 'flag' | 'end' | 'event' | 'alert'

const MAP_CARD_TONE: Record<MapCardTone, { icon: string, pill: string }> = {
  amber: { icon: 'bg-amber-100 text-amber-700', pill: 'bg-amber-100 text-amber-800' },
  purple: { icon: 'bg-purple-100 text-purple-700', pill: 'bg-purple-100 text-purple-800' },
  green: { icon: 'bg-green-100 text-green-700', pill: 'bg-green-100 text-green-800' },
  red: { icon: 'bg-red-100 text-red-700', pill: 'bg-red-100 text-red-800' },
  blue: { icon: 'bg-blue-100 text-blue-700', pill: 'bg-blue-100 text-blue-800' }
}

const MAP_CARD_GLYPH: Record<MapCardGlyph, string> = {
  pause: '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/></svg>',
  p: '<span class="text-sm font-bold leading-none">P</span>',
  flag: '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M5 3v18h2V14h5l1 2h6V5h-5l-1-2H5z"/></svg>',
  end: '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M4 4h16v4H4zm0 6h16v4H4zm0 6h16v4H4z"/></svg>',
  event: '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="12" cy="12" r="4"/></svg>',
  alert: '<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M12 3l10 18H2L12 3zm0 6v5h0.01M12 17h.01" stroke="currentColor" stroke-width="2" fill="none"/></svg>'
}

/** "MOVEMENT_START" and "MOVEMENT START" both become "Movement start". */
function humanEventLabel(raw: string): string {
  const words = raw.replace(/_/g, ' ').trim().toLowerCase()
  if (!words) return 'Event'
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/**
 * The sentence worth showing under the title. A vendor frame such as
 * "Teltonika: MOVEMENT [IO=240] [IGNITION_ON] [MOVING]" only repeats the
 * title in machine form, so it is dropped. A geofence sentence is kept,
 * without the shortened vehicle name the card already shows in full.
 */
function usefulEventDetail(description: unknown, title: string): string | null {
  const text = String(description ?? '').trim()
  if (!text) return null
  if (/\[[A-Z0-9_]+=?-?\d*\]/.test(text)) return null
  const geofence = text.match(/(entered|exited|left) geofence\s+"?([^"]+)"?/i)
  if (geofence) {
    const verb = geofence[1].toLowerCase() === 'entered' ? 'Entered' : 'Left'
    return `${verb} geofence ${geofence[2].trim()}`
  }
  if (text.replace(/_/g, ' ').toLowerCase() === title.toLowerCase()) return null
  return text
}

function humanStatus(status: unknown): string | null {
  const text = String(status ?? '').trim()
  if (!text || text.toUpperCase() === text && text.length > 24) return null
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()
}

function revealPlace(placeLine: HTMLElement, coordsLine: HTMLElement, latitude: number, longitude: number) {
  if (placeLine.dataset.resolved === '1') return
  resolvePlaceName(latitude, longitude)
    .then(name => {
      placeLine.dataset.resolved = '1'
      if (name) {
        placeLine.textContent = name
        coordsLine.classList.remove('hidden')
      }
    })
    .catch(() => { placeLine.dataset.resolved = '1' })
}

type MapCard = { element: HTMLElement, placeLine: HTMLElement, coordsLine: HTMLElement }

/** One layout for every marker popup: title, vehicle, facts, place. */
function buildMapCard(info: {
  tone: MapCardTone
  glyph: MapCardGlyph
  title: string
  aside?: string
  vehicleName: string
  vehicleNumber: string
  rows?: Array<[string, string]>
  latitude: number
  longitude: number
  detail?: string | null
  notes?: string[]
  pills?: string[]
}): MapCard {
  const tone = MAP_CARD_TONE[info.tone]
  const root = cardNode('div', 'w-[280px] font-sans text-slate-800')

  const header = cardNode('div', 'flex items-start gap-2.5')
  const badge = cardNode('div', `w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${tone.icon}`)
  badge.innerHTML = MAP_CARD_GLYPH[info.glyph]
  header.appendChild(badge)

  const titles = cardNode('div', 'min-w-0 flex-1')
  const titleRow = cardNode('div', 'flex items-center justify-between gap-2')
  titleRow.appendChild(cardNode('div', 'text-sm font-semibold text-slate-900 truncate', info.title))
  if (info.aside) titleRow.appendChild(cardNode('div', 'text-sm font-bold tabular-nums text-slate-900 shrink-0', info.aside))
  titles.appendChild(titleRow)
  if (info.vehicleName) titles.appendChild(cardNode('div', 'text-[13px] font-semibold text-slate-800 truncate', info.vehicleName))
  if (info.vehicleNumber && info.vehicleNumber !== info.vehicleName) {
    titles.appendChild(cardNode('div', 'text-[11px] text-slate-500 truncate', info.vehicleNumber))
  }
  header.appendChild(titles)
  root.appendChild(header)

  if (info.rows && info.rows.length > 0) {
    const facts = cardNode('div', `mt-3 grid gap-3 ${info.rows.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`)
    for (const [label, value] of info.rows) {
      const cell = cardNode('div', '')
      cell.appendChild(cardNode('div', 'text-[10px] font-semibold uppercase tracking-wide text-slate-400', label))
      cell.appendChild(cardNode('div', 'mt-0.5 text-xs text-slate-800', value))
      facts.appendChild(cell)
    }
    root.appendChild(facts)
  }

  const place = cardNode('div', 'mt-3 pt-2.5 border-t border-slate-100')
  place.appendChild(cardNode('div', 'text-[10px] font-semibold uppercase tracking-wide text-slate-400', 'Location'))
  const placeLine = cardNode('div', 'mt-0.5 text-xs font-medium text-slate-800', `${info.latitude.toFixed(5)}, ${info.longitude.toFixed(5)}`)
  const coordsLine = cardNode('div', 'hidden text-[10px] text-slate-400', `${info.latitude.toFixed(5)}, ${info.longitude.toFixed(5)}`)
  place.appendChild(placeLine)
  place.appendChild(coordsLine)
  root.appendChild(place)

  if (info.detail) {
    root.appendChild(cardNode('div', 'mt-2 text-xs text-slate-600', info.detail))
  }
  if (info.notes && info.notes.length > 0) {
    const list = cardNode('div', 'mt-2 space-y-1')
    info.notes.forEach(note => list.appendChild(cardNode('div', 'text-[11px] text-slate-600 leading-snug', note)))
    root.appendChild(list)
  }
  if (info.pills && info.pills.length > 0) {
    const footer = cardNode('div', 'mt-2.5 flex flex-wrap items-center gap-1.5')
    info.pills.forEach((pill, index) => {
      footer.appendChild(cardNode(
        'span',
        index === 0
          ? `px-1.5 py-0.5 rounded text-[10px] font-semibold ${tone.pill}`
          : 'px-1.5 py-0.5 rounded text-[10px] font-medium bg-slate-100 text-slate-600',
        pill
      ))
    })
    root.appendChild(footer)
  }

  return { element: root, placeLine, coordsLine }
}

function buildStopCard(info: {
  index: number
  isLongParking: boolean
  durationLabel: string
  startLabel: string
  endLabel: string
  latitude: number
  longitude: number
  vehicleName: string
  vehicleNumber: string
  batteryLevel?: number
  signalStrength?: string | number
}): MapCard {
  const label = info.isLongParking ? 'Parking' : 'Short stop'
  const pills = [label]
  if (info.batteryLevel != null) pills.push(`Battery ${info.batteryLevel}%`)
  if (info.signalStrength) pills.push(`Signal ${info.signalStrength}`)
  return buildMapCard({
    tone: info.isLongParking ? 'purple' : 'amber',
    glyph: info.isLongParking ? 'p' : 'pause',
    title: `${label} ${info.index}`,
    aside: info.durationLabel,
    vehicleName: info.vehicleName,
    vehicleNumber: info.vehicleNumber,
    rows: [['Start', info.startLabel], ['End', info.endLabel]],
    latitude: info.latitude,
    longitude: info.longitude,
    pills
  })
}

// Format duration for display
function formatDuration(hours: number): string {
  if (hours < 1) {
    const minutes = Math.round(hours * 60)
    return `${minutes} min`
  } else if (hours < 24) {
    const wholeHours = Math.floor(hours)
    const minutes = Math.round((hours - wholeHours) * 60)
    return minutes > 0 ? `${wholeHours}h ${minutes}min` : `${wholeHours}h`
  } else {
    const days = Math.floor(hours / 24)
    const remainingHours = Math.floor(hours % 24)
    return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days}d`
  }
}

// Calculate trajectory statistics
function calculateTrajectoryStats(points: any[], stopEvents: any[]) {
  if (points.length === 0) {
    return {
      totalDistance: 0,
      totalDuration: 0,
      startTime: null,
      endTime: null,
      maxSpeed: 0,
      avgSpeed: 0,
      stopCount: 0
    }
  }

  // Calculate total distance
  let totalDistance = 0
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1]
    const curr = points[i]
    totalDistance += calculateDistance(prev.lat, prev.lng, curr.lat, curr.lng)
  }

  // Calculate duration in minutes
  const startTime = points[0].timestamp
  const endTime = points[points.length - 1].timestamp
  const totalDuration = (endTime.getTime() - startTime.getTime()) / (1000 * 60)

  // Calculate speed statistics
  const speeds = points.map(p => p.speed).filter(s => s > 0)
  const maxSpeed = Math.max(...speeds, 0)
  const avgSpeed = speeds.length > 0 ? speeds.reduce((sum, speed) => sum + speed, 0) / speeds.length : 0

  return {
    totalDistance,
    totalDuration,
    startTime,
    endTime,
    maxSpeed,
    avgSpeed,
    stopCount: stopEvents.length
  }
}

// Calculate distance between two GPS points (in km)
function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371 // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180
  const dLon = (lon2 - lon1) * Math.PI / 180
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

export default ResponsiveFleetMap
